import React, { useState, useEffect, useRef, useMemo } from 'react';
import { getAdminUsers, approveUser, deleteUser, generateResetCode, uploadUserPhoto,
  getUsersWithSubscriptions, getUserPaymentHistory, renewSubscription, setUserFree,
  setUserExpiry, blockUser, suspendUser, setUserTrial, handleUserRequest, setUserManagementType,
  setUserDelegationType, sendQuickActionMessage, getUserCompletedCycles, sendBroadcastMessage,
  resetUserTelegram, endUserTrial, testTelegramNotifications, toggleUserStatus,
  getDashboardCredentials, updateDashboardCredentials,
  getAdminAggregateSummary, getAdminAggregatePreferences, updateAdminAggregatePreferences } from '../../lib/api';
import { ProfileGroupModal } from './components/ProfileGroupModal';
import { FamilyGroupModal } from './components/FamilyGroupModal';
import { AggregateSummaryCard } from './components/AggregateSummaryCard';
import { UserCard } from './components/UserCard';
import { SecondaryAdminCard } from './components/SecondaryAdminCard';
import { buildGroupColorMap, GROUP_PALETTE, PRIMARY_COLOR } from './components/groupColors';
import { sortProfilesForDisplay } from '../../lib/profileOrder';
import axios from 'axios';
// Use relative URL for API calls to avoid CORS issues in production
const API = '';
import { useModule } from '../../contexts/ModuleContext';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { formatCurrency, formatDate } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { RefreshCw, Check, Trash2, LogIn, Clock, TrendingUp, Calendar, User, Key, Copy, Camera, 
  CreditCard, Ban, Infinity, History, Plus, MessageCircle, Pause, Play, AlertTriangle, DollarSign, 
  CheckCircle, XCircle, Timer, Users, Send, Bell, Search, Filter, X, Eye, EyeOff, Save, FileText, Power,
  ChevronDown, ChevronRight, Wallet, Percent, Gift, ExternalLink, Link2, Home , FolderOpen , Coins, MoreHorizontal, Settings, Building2 } from 'lucide-react';

// 1D: voci del menu laterale della scheda utente interna (solo aspetto)
const USER_PANELS = [
  { id: 'telegram', label: 'Telegram', short: 'Telegram', icon: Send, warn: ({ selectedUser }) => !selectedUser?.telegram_chat_id },
  { id: 'collegamenti', label: 'Collegamenti e famiglia', short: 'Famiglia', icon: Link2,
    count: ({ selectedUser, users }) => selectedUser?.family_group_id ? users.filter(u => u.family_group_id === selectedUser.family_group_id).length : null },
  { id: 'impostazioni', label: 'Impostazioni', short: 'Impost.', icon: Settings },
  { id: 'profili', label: 'Profili Arbicsx', short: 'Profili', icon: Key, count: ({ arbicsxAccounts }) => arbicsxAccounts?.length || null },
  { id: 'broker', label: 'Broker & FP Trading', short: 'Broker', icon: Building2 },
  { id: 'wallet', label: 'Crypto Wallet', short: 'Wallet', icon: Coins },
  { id: 'compenso', label: 'Compenso Gestore', short: 'Compenso', icon: DollarSign,
    warn: ({ selectedUser }) => (selectedUser?.compenso_da_pagare_usd || 0) > 0 },
];
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';

export default function ArbicsxUtenti() {
  const { startImpersonation, isAdminPrimary, isAdminSecondary } = useModule();
  const { isAdmin, user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteUserId, setDeleteUserId] = useState(null);
  const [deleteUserName, setDeleteUserName] = useState('');
  
  // Reset code modal state
  const [showResetCodeModal, setShowResetCodeModal] = useState(false);
  const [resetCodeData, setResetCodeData] = useState({ code: '', userName: '', expiresAt: '' });
  const [generatingCode, setGeneratingCode] = useState(false);
  
  // Photo upload state
  const [uploadingPhotoFor, setUploadingPhotoFor] = useState(null);
  const fileInputRefs = useRef({});
  
  // Subscription management state
  const [showRenewModal, setShowRenewModal] = useState(false);
  const [renewUserId, setRenewUserId] = useState(null);
  const [renewUserName, setRenewUserName] = useState('');
  const [renewMonths, setRenewMonths] = useState(1);
  const [renewAmount, setRenewAmount] = useState('');
  const [renewing, setRenewing] = useState(false);
  
  const [showPaymentHistory, setShowPaymentHistory] = useState(false);
  const [paymentHistoryUser, setPaymentHistoryUser] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState({ payments: [], total_paid: 0 });
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  const [showExpiryModal, setShowExpiryModal] = useState(false);
  const [expiryUserId, setExpiryUserId] = useState(null);
  const [expiryUserName, setExpiryUserName] = useState('');
  const [newExpiryDate, setNewExpiryDate] = useState('');
  
  // Trial period modal state
  const [showTrialModal, setShowTrialModal] = useState(false);
  const [trialUserId, setTrialUserId] = useState(null);
  const [trialUserName, setTrialUserName] = useState('');
  const [trialDays, setTrialDays] = useState(15);
  const [settingTrial, setSettingTrial] = useState(false);
  
  // End trial confirmation state
  const [showEndTrialConfirm, setShowEndTrialConfirm] = useState(false);
  const [endTrialUserId, setEndTrialUserId] = useState(null);
  const [endTrialUserName, setEndTrialUserName] = useState('');
  const [endingTrial, setEndingTrial] = useState(false);
  
  // Reset Telegram confirmation state
  const [showProfileGroupModal, setShowProfileGroupModal] = useState(false);
  const [showFamilyGroupModal, setShowFamilyGroupModal] = useState(false);
  const [showResetTelegramConfirm, setShowResetTelegramConfirm] = useState(false);
  const [resetTelegramUser, setResetTelegramUser] = useState(null);
  const [resettingTelegram, setResettingTelegram] = useState(false);

  // Link Telegram state
  const [showLinkTelegramModal, setShowLinkTelegramModal] = useState(false);
  const [linkTelegramUser, setLinkTelegramUser] = useState(null);
  const [linkTelegramChatId, setLinkTelegramChatId] = useState('');
  const [linkingTelegram, setLinkingTelegram] = useState(false);

  // Quick action modal state
  const [showQuickActionModal, setShowQuickActionModal] = useState(false);
  const [quickActionType, setQuickActionType] = useState('withdrawal'); // 'withdrawal' or 'purchase'
  const [quickActionUser, setQuickActionUser] = useState(null);
  const [quickActionAmount, setQuickActionAmount] = useState('');

  // Broadcast message modal state
  const [showBroadcastModal, setShowBroadcastModal] = useState(false);
  const [broadcastTarget, setBroadcastTarget] = useState('all'); // 'all', 'managed', 'autonomous', 'active', 'selected'
  const [broadcastMessage, setBroadcastMessage] = useState('');
  const [sendingBroadcast, setSendingBroadcast] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState([]); // For specific user selection
  const [quickActionNumAccounts, setQuickActionNumAccounts] = useState(1);
  const [quickActionMethod, setQuickActionMethod] = useState('crypto');
  const [quickActionWho, setQuickActionWho] = useState('admin');
  const [sendingQuickAction, setSendingQuickAction] = useState(false);

  // User details modal state
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Dashboard credentials state (legacy - kept for compatibility)
  const [dashboardEmail, setDashboardEmail] = useState('');
  const [dashboardPassword, setDashboardPassword] = useState('');
  const [showDashboardPassword, setShowDashboardPassword] = useState(false);
  const [savingCredentials, setSavingCredentials] = useState(false);
  const [loadingCredentials, setLoadingCredentials] = useState(false);

  // Broker account state (legacy - kept for retrocompatibility)
  const [brokerAccountNumber, setBrokerAccountNumber] = useState('');
  const [savingBrokerAccount, setSavingBrokerAccount] = useState(false);
  
  // Broker account per-profile state
  const [accountBrokerNumbers, setAccountBrokerNumbers] = useState({});
  const [savingAccountBroker, setSavingAccountBroker] = useState({});

  // TrustWallet address state
  const [trustwalletAddress, setTrustwalletAddress] = useState('');
  const [savingTrustwalletAddress, setSavingTrustwalletAddress] = useState(false);

  // FP Trading credentials state
  const [fpEmail, setFpEmail] = useState('');
  const [fpPassword, setFpPassword] = useState('');
  const [fpPin, setFpPin] = useState('');
  const [showFpPassword, setShowFpPassword] = useState(false);
  const [showFpPin, setShowFpPin] = useState(false);
  const [savingFpCredentials, setSavingFpCredentials] = useState(false);

  // Report generation state
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportUserId, setReportUserId] = useState(null);
  const [reportUserName, setReportUserName] = useState('');
  const [reportPeriodType, setReportPeriodType] = useState('full'); // 'full' or 'custom'
  const [reportDateFrom, setReportDateFrom] = useState('');
  const [reportDateTo, setReportDateTo] = useState('');
  const [reportTitle, setReportTitle] = useState('Report Storico Completo');
  const [generatingReport, setGeneratingReport] = useState(false);

  // Collapsible sections state
  const [expandedSections, setExpandedSections] = useState({
    profiliArbicsx: false,
    brokerFP: false,
    cryptoWallet: false,
    compensoGestore: false
  });

  // 1D: pannello visibile nella scheda interna (predefinito: Impostazioni)
  const [activePanel, setActivePanel] = useState('impostazioni');

  // Compenso Gestore state
  const [compensoData, setCompensoData] = useState({
    compenso_attivo: false,
    compenso_percentuale: 0,
    compenso_data_inizio: '',
    franchigia_eur: 250,
    profitti_soggetti: 0,
    compenso_maturato_eur: 0,
    compenso_maturato_usd: 0,
    compenso_pagato_totale: 0,
    free_periods: [],
    free_period_attivo: null
  });
  const [savingCompenso, setSavingCompenso] = useState(false);
  const [loadingCompenso, setLoadingCompenso] = useState(false);
  const [reportSendToAdmin, setReportSendToAdmin] = useState(false);
  
  // Free period form state
  const [newFreePeriod, setNewFreePeriod] = useState({ start_date: '', end_date: '', label: '' });
  const [addingFreePeriod, setAddingFreePeriod] = useState(false);
  const [removingFreePeriod, setRemovingFreePeriod] = useState(null);

  // Compenso payments state
  const [compensoPagamenti, setCompensoPagamenti] = useState([]);
  const [loadingPagamenti, setLoadingPagamenti] = useState(false);
  const [showPagamentiList, setShowPagamentiList] = useState(false);

  // Profit reset date state
  const [savingProfitReset, setSavingProfitReset] = useState(false);

  // Global report state
  const [showGlobalReportModal, setShowGlobalReportModal] = useState(false);
  const [globalReportPeriodType, setGlobalReportPeriodType] = useState('full');
  const [globalReportDateFrom, setGlobalReportDateFrom] = useState('');
  const [globalReportDateTo, setGlobalReportDateTo] = useState(new Date().toISOString().split('T')[0]);
  const [globalReportTitle, setGlobalReportTitle] = useState('Report Globale — Tutti gli Utenti');
  const [generatingGlobalReport, setGeneratingGlobalReport] = useState(false);

  // Arbicsx multi-account state
  const [arbicsxAccounts, setArbicsxAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [newAccountForm, setNewAccountForm] = useState({ email: '', password: '', label: '' });
  const [showNewAccountForm, setShowNewAccountForm] = useState(false);
  const [savingAccount, setSavingAccount] = useState(false);
  const [deletingAccountId, setDeletingAccountId] = useState(null);
  const [showAccountPasswords, setShowAccountPasswords] = useState({});

  // Search state
  const [searchQuery, setSearchQuery] = useState('');

  // Aggregate Summary state (admin only)
  const [aggregateSummary, setAggregateSummary] = useState(null);
  const [loadingAggregate, setLoadingAggregate] = useState(false);
  const [showAggregateSelector, setShowAggregateSelector] = useState(false);
  const [aggregateSelectedIds, setAggregateSelectedIds] = useState([]);

  // Completed cycles filter state
  const [cycleFilters, setCycleFilters] = useState({
    propFirm: '',
    monthYear: '',
    type: '', // 'burned' or 'profit_prop'
    result: '' // 'positive' or 'negative'
  });

  // Completed cycles data
  const [completedCycles, setCompletedCycles] = useState([]);
  const [completedCyclesFilterOptions, setCompletedCyclesFilterOptions] = useState({ prop_firms: [], month_years: [] });
  const [loadingCompletedCycles, setLoadingCompletedCycles] = useState(false);
  const [showCompletedCyclesPanel, setShowCompletedCyclesPanel] = useState(false);

  // Load completed cycles for a user
  const loadCompletedCycles = async (userId) => {
    setLoadingCompletedCycles(true);
    try {
      const response = await getUserCompletedCycles(userId);
      setCompletedCycles(response.data.cycles || []);
      setCompletedCyclesFilterOptions(response.data.filter_options || { prop_firms: [], month_years: [] });
    } catch (error) {
      console.error('Error loading completed cycles:', error);
      toast.error('Errore nel caricamento cicli completati');
    } finally {
      setLoadingCompletedCycles(false);
    }
  };

  // Filter completed cycles
  const filteredCompletedCycles = useMemo(() => {
    return completedCycles.filter(cycle => {
      // Filter by prop firm
      if (cycleFilters.propFirm && cycle.prop_firm !== cycleFilters.propFirm) return false;
      
      // Filter by month/year
      if (cycleFilters.monthYear) {
        const cycleDate = cycle.end_date || cycle.start_date || '';
        if (!cycleDate.startsWith(cycleFilters.monthYear)) return false;
      }
      
      // Filter by type (burned vs profit_prop)
      if (cycleFilters.type) {
        const hasProfitProp = cycle.went_through_awaiting || cycle.payout_type;
        if (cycleFilters.type === 'profit_prop' && !hasProfitProp) return false;
        if (cycleFilters.type === 'burned' && hasProfitProp) return false;
      }
      
      // Filter by result (positive vs negative)
      if (cycleFilters.result) {
        const netProfit = cycle.total_net_profit || 0;
        if (cycleFilters.result === 'positive' && netProfit <= 0) return false;
        if (cycleFilters.result === 'negative' && netProfit >= 0) return false;
      }
      
      return true;
    });
  }, [completedCycles, cycleFilters]);

  // Open user details modal
  const openUserDetails = async (user) => {
    setSelectedUser(user);
    setShowUserDetails(true);
    setActivePanel('impostazioni');
    // Reset cycle filters and data when opening a new user
    setCycleFilters({ propFirm: '', monthYear: '', type: '', result: '' });
    setCompletedCycles([]);
    setShowCompletedCyclesPanel(false);
    // Reset credentials state (legacy)
    setDashboardEmail('');
    setDashboardPassword('');
    setLoadingCredentials(true);
    // Reset multi-account state
    setArbicsxAccounts([]);
    setShowNewAccountForm(false);
    setNewAccountForm({ email: '', password: '', label: '' });
    setShowAccountPasswords({});
    setLoadingAccounts(true);
    // Reset collapsible sections
    setExpandedSections({
      profiliArbicsx: false,
      brokerFP: false,
      cryptoWallet: false,
      compensoGestore: false
    });
    
    // Initialize broker account from user data
    setBrokerAccountNumber(user.broker_account_number || '');
    
    // Initialize TrustWallet address (now called Crypto Wallet)
    setTrustwalletAddress(user.trustwallet_address || '');
    
    // Initialize FP Trading credentials
    setFpEmail(user.fp_email || '');
    setFpPassword(user.fp_password || '');
    setFpPin(user.fp_pin || '');
    
    try {
      // Load legacy credentials (for backwards compatibility)
      const credResponse = await getDashboardCredentials(user.id);
      setDashboardEmail(credResponse.data.arbicsx_email || '');
      setDashboardPassword(credResponse.data.arbicsx_password || '');
    } catch (error) {
      console.error('Error loading credentials:', error);
    } finally {
      setLoadingCredentials(false);
    }
    
    // Load Arbicsx accounts
    try {
      const accountsResponse = await axios.get(`${API}/api/arbicsx/users/${user.id}/accounts`);
      const accounts = accountsResponse.data.accounts || [];
      setArbicsxAccounts(accounts);
      // Initialize broker account numbers per profile
      const brokerNumbers = {};
      accounts.forEach(acc => {
        brokerNumbers[acc.id] = acc.broker_account_number || '';
      });
      setAccountBrokerNumbers(brokerNumbers);
    } catch (error) {
      console.error('Error loading Arbicsx accounts:', error);
    } finally {
      setLoadingAccounts(false);
    }

    // Load Compenso summary
    loadCompensoSummary(user.id);
  };

  // Close user details modal
  const closeUserDetails = () => {
    setShowUserDetails(false);
    setSelectedUser(null);
    setCycleFilters({ propFirm: '', monthYear: '', type: '', result: '' });
    setCompletedCycles([]);
    setShowCompletedCyclesPanel(false);
    setDashboardEmail('');
    setDashboardPassword('');
    setShowDashboardPassword(false);
    setBrokerAccountNumber('');
    setTrustwalletAddress('');
    setFpEmail('');
    setFpPassword('');
    setFpPin('');
    setShowFpPassword(false);
    setShowFpPin(false);
  };

  // Save dashboard credentials
  const handleSaveCredentials = async () => {
    if (!selectedUser) return;
    setSavingCredentials(true);
    try {
      await updateDashboardCredentials(selectedUser.id, dashboardEmail, dashboardPassword);
      toast.success('Credenziali salvate con successo');
    } catch (error) {
      toast.error('Errore nel salvataggio credenziali');
    } finally {
      setSavingCredentials(false);
    }
  };

  // Save broker account number (legacy - kept for retrocompatibility)
  const handleSaveBrokerAccount = async () => {
    if (!selectedUser) return;
    setSavingBrokerAccount(true);
    try {
      await axios.put(`${API}/api/arbicsx/users/${selectedUser.id}/broker-account`, {
        broker_account_number: brokerAccountNumber
      });
      toast.success('Account Broker salvato con successo');
    } catch (error) {
      toast.error('Errore nel salvataggio Account Broker');
    } finally {
      setSavingBrokerAccount(false);
    }
  };

  // Save broker account number for specific Arbicsx profile
  const handleSaveAccountBroker = async (accountId) => {
    if (!selectedUser) return;
    setSavingAccountBroker(prev => ({ ...prev, [accountId]: true }));
    try {
      await axios.put(`${API}/api/arbicsx/users/${selectedUser.id}/accounts/${accountId}/broker-account`, {
        broker_account_number: accountBrokerNumbers[accountId] || ''
      });
      // Update local state
      setArbicsxAccounts(prev => prev.map(acc => 
        acc.id === accountId 
          ? { ...acc, broker_account_number: accountBrokerNumbers[accountId] || '' }
          : acc
      ));
      toast.success('N° Conto Broker salvato');
    } catch (error) {
      toast.error('Errore nel salvataggio N° Conto Broker');
    } finally {
      setSavingAccountBroker(prev => ({ ...prev, [accountId]: false }));
    }
  };

  // Save TrustWallet address
  const handleSaveTrustwalletAddress = async () => {
    if (!selectedUser) return;
    setSavingTrustwalletAddress(true);
    try {
      await axios.put(`${API}/api/arbicsx/users/${selectedUser.id}/trustwallet-address`, {
        trustwallet_address: trustwalletAddress
      });
      toast.success('Indirizzo TrustWallet salvato con successo');
    } catch (error) {
      toast.error('Errore nel salvataggio indirizzo TrustWallet');
    } finally {
      setSavingTrustwalletAddress(false);
    }
  };

  // Save FP Trading credentials
  const handleSaveFpCredentials = async () => {
    if (!selectedUser) return;
    setSavingFpCredentials(true);
    try {
      await axios.put(`${API}/api/arbicsx/users/${selectedUser.id}/fp-credentials`, {
        fp_email: fpEmail,
        fp_password: fpPassword,
        fp_pin: fpPin
      });
      toast.success('Credenziali FP Trading salvate con successo');
    } catch (error) {
      toast.error('Errore nel salvataggio credenziali FP Trading');
    } finally {
      setSavingFpCredentials(false);
    }
  };

  // Toggle collapsible section
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Load compenso summary for selected user
  const loadCompensoSummary = async (userId) => {
    setLoadingCompenso(true);
    try {
      const response = await axios.get(`${API}/api/arbicsx/users/${userId}/compenso-summary`);
      setCompensoData(response.data);
    } catch (error) {
      console.error('Error loading compenso summary:', error);
    } finally {
      setLoadingCompenso(false);
    }
  };

  // Save compenso settings
  const handleSaveCompensoSettings = async () => {
    if (!selectedUser) return;
    setSavingCompenso(true);
    try {
      await axios.put(`${API}/api/arbicsx/users/${selectedUser.id}/compenso-settings`, {
        compenso_attivo: compensoData.compenso_attivo,
        compenso_percentuale: compensoData.compenso_percentuale,
        compenso_data_inizio: compensoData.compenso_data_inizio || null
      });
      toast.success('Impostazioni compenso salvate');
      // Reload summary to get updated calculations
      await loadCompensoSummary(selectedUser.id);
    } catch (error) {
      toast.error('Errore nel salvataggio compenso');
    } finally {
      setSavingCompenso(false);
    }
  };

  // Request compenso payment
  const handleRichiestaCompenso = async () => {
    if (!selectedUser) return;
    try {
      await axios.post(`${API}/api/arbicsx/users/${selectedUser.id}/compenso-richiedi`);
      toast.success('Richiesta compenso inviata');
      await loadCompensoSummary(selectedUser.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella richiesta');
    }
  };

  // Add free period
  const handleAddFreePeriod = async () => {
    if (!selectedUser || !newFreePeriod.start_date || !newFreePeriod.end_date) {
      toast.error('Seleziona data inizio e fine');
      return;
    }
    if (newFreePeriod.end_date <= newFreePeriod.start_date) {
      toast.error('La data fine deve essere dopo la data inizio');
      return;
    }
    setAddingFreePeriod(true);
    try {
      await axios.post(`${API}/api/arbicsx/users/${selectedUser.id}/add-free-period`, {
        start_date: newFreePeriod.start_date,
        end_date: newFreePeriod.end_date,
        label: newFreePeriod.label || ''
      });
      toast.success('Periodo gratuito aggiunto');
      setNewFreePeriod({ start_date: '', end_date: '', label: '' });
      await loadCompensoSummary(selectedUser.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'aggiunta del periodo');
    } finally {
      setAddingFreePeriod(false);
    }
  };

  // Remove free period
  const handleRemoveFreePeriod = async (startDate) => {
    if (!selectedUser) return;
    setRemovingFreePeriod(startDate);
    try {
      await axios.delete(`${API}/api/arbicsx/users/${selectedUser.id}/remove-free-period`, {
        data: { start_date: startDate }
      });
      toast.success('Periodo gratuito rimosso');
      await loadCompensoSummary(selectedUser.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella rimozione');
    } finally {
      setRemovingFreePeriod(null);
    }
  };

  // Format date for display (DD/MM/YYYY)
  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return dateStr;
  };

  // Check if user has an active free period today
  const getActiveFreePeriod = (user) => {
    if (!user.free_periods || user.free_periods.length === 0) return null;
    const today = new Date().toISOString().split('T')[0];
    for (const fp of user.free_periods) {
      if (fp.start_date && fp.end_date && fp.start_date <= today && today <= fp.end_date) {
        return fp;
      }
    }
    return null;
  };

  // Load compenso payments for selected user
  const loadCompensoPagamenti = async (userId) => {
    setLoadingPagamenti(true);
    try {
      const response = await axios.get(`${API}/api/arbicsx/users/${userId}/compenso-pagamenti`);
      setCompensoPagamenti(response.data.payments || []);
    } catch (error) {
      console.error('Error loading compenso payments:', error);
      setCompensoPagamenti([]);
    } finally {
      setLoadingPagamenti(false);
    }
  };

  // Get Etherscan link for tx hash
  const getEtherscanLink = (txHash) => {
    if (!txHash) return null;
    return `https://etherscan.io/tx/${txHash}`;
  };

  // Set profit reset date for user
  const handleSetProfitResetDate = async (userId, resetDate) => {
    setSavingProfitReset(true);
    try {
      await axios.post(`${API}/api/arbicsx/users/${userId}/set-profit-reset-date`, {
        profit_reset_date: resetDate || null
      });
      toast.success(resetDate ? 'Data reset profitti impostata' : 'Data reset profitti rimossa');
      // Update local state
      setUsers(users.map(u => u.id === userId ? { ...u, profit_reset_date: resetDate || null } : u));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'impostazione');
    } finally {
      setSavingProfitReset(false);
    }
  };

  // Open report generation modal
  const openReportModal = (user) => {
    setReportUserId(user.id);
    setReportUserName(user.name || 'Utente');
    setReportPeriodType('full');
    setReportDateFrom('');
    setReportDateTo(new Date().toISOString().split('T')[0]);
    setReportTitle('Report Storico Completo');
    setReportSendToAdmin(false);
    setShowReportModal(true);
  };

  // Handle period type change
  const handlePeriodTypeChange = (type) => {
    setReportPeriodType(type);
    if (type === 'full') {
      setReportTitle('Report Storico Completo');
    } else {
      const fromFormatted = reportDateFrom ? new Date(reportDateFrom).toLocaleDateString('it-IT') : '';
      const toFormatted = reportDateTo ? new Date(reportDateTo).toLocaleDateString('it-IT') : new Date().toLocaleDateString('it-IT');
      setReportTitle(`Report dal ${fromFormatted} al ${toFormatted}`);
    }
  };

  // Update title when dates change
  const handleDateChange = (field, value) => {
    if (field === 'from') {
      setReportDateFrom(value);
      if (reportPeriodType === 'custom') {
        const fromFormatted = value ? new Date(value).toLocaleDateString('it-IT') : '';
        const toFormatted = reportDateTo ? new Date(reportDateTo).toLocaleDateString('it-IT') : new Date().toLocaleDateString('it-IT');
        setReportTitle(`Report dal ${fromFormatted} al ${toFormatted}`);
      }
    } else {
      setReportDateTo(value);
      if (reportPeriodType === 'custom') {
        const fromFormatted = reportDateFrom ? new Date(reportDateFrom).toLocaleDateString('it-IT') : '';
        const toFormatted = value ? new Date(value).toLocaleDateString('it-IT') : new Date().toLocaleDateString('it-IT');
        setReportTitle(`Report dal ${fromFormatted} al ${toFormatted}`);
      }
    }
  };

  // Generate and send report
  const handleGenerateReport = async () => {
    if (!reportUserId) return;
    setGeneratingReport(true);
    try {
      const payload = {
        title: reportTitle,
        date_from: reportPeriodType === 'custom' ? reportDateFrom : null,
        date_to: reportPeriodType === 'custom' ? reportDateTo : null,
        send_to_admin: reportSendToAdmin
      };
      const response = await axios.post(`${API}/api/arbicsx/users/${reportUserId}/generate-report`, payload);
      if (response.data.success) {
        const recipient = response.data.sent_to_admin ? 'ad admin' : 'all\'utente';
        toast.success(`Report inviato su Telegram ${recipient} ✅ (${response.data.profits_count} profitti)`);
        setShowReportModal(false);
      } else {
        toast.error(response.data.error || 'Errore nella generazione del report');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella generazione del report');
    } finally {
      setGeneratingReport(false);
    }
  };

  // Open global report modal
  const openGlobalReportModal = () => {
    setGlobalReportPeriodType('full');
    setGlobalReportDateFrom('');
    setGlobalReportDateTo(new Date().toISOString().split('T')[0]);
    setGlobalReportTitle('Report Globale — Tutti gli Utenti');
    setShowGlobalReportModal(true);
  };

  // Handle global report period type change
  const handleGlobalPeriodTypeChange = (type) => {
    setGlobalReportPeriodType(type);
    if (type === 'full') {
      setGlobalReportTitle('Report Globale — Tutti gli Utenti');
    } else {
      const fromFormatted = globalReportDateFrom ? new Date(globalReportDateFrom).toLocaleDateString('it-IT') : '';
      const toFormatted = globalReportDateTo ? new Date(globalReportDateTo).toLocaleDateString('it-IT') : new Date().toLocaleDateString('it-IT');
      setGlobalReportTitle(`Report Globale dal ${fromFormatted} al ${toFormatted}`);
    }
  };

  // Handle global report date change
  const handleGlobalDateChange = (field, value) => {
    if (field === 'from') {
      setGlobalReportDateFrom(value);
      if (globalReportPeriodType === 'custom') {
        const fromFormatted = value ? new Date(value).toLocaleDateString('it-IT') : '';
        const toFormatted = globalReportDateTo ? new Date(globalReportDateTo).toLocaleDateString('it-IT') : new Date().toLocaleDateString('it-IT');
        setGlobalReportTitle(`Report Globale dal ${fromFormatted} al ${toFormatted}`);
      }
    } else {
      setGlobalReportDateTo(value);
      if (globalReportPeriodType === 'custom') {
        const fromFormatted = globalReportDateFrom ? new Date(globalReportDateFrom).toLocaleDateString('it-IT') : '';
        const toFormatted = value ? new Date(value).toLocaleDateString('it-IT') : new Date().toLocaleDateString('it-IT');
        setGlobalReportTitle(`Report Globale dal ${fromFormatted} al ${toFormatted}`);
      }
    }
  };

  // Generate and send global report
  const handleGenerateGlobalReport = async () => {
    setGeneratingGlobalReport(true);
    try {
      const payload = {
        title: globalReportTitle,
        date_from: globalReportPeriodType === 'custom' ? globalReportDateFrom : null,
        date_to: globalReportPeriodType === 'custom' ? globalReportDateTo : null
      };
      const response = await axios.post(`${API}/api/arbicsx/reports/global`, payload);
      if (response.data.success) {
        toast.success(`Report globale inviato su Telegram (${response.data.users_count} utenti, ${response.data.profits_count} profitti)`);
        setShowGlobalReportModal(false);
      } else {
        toast.error(response.data.error || 'Errore nella generazione del report');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella generazione del report');
    } finally {
      setGeneratingGlobalReport(false);
    }
  };

  // Add new Arbicsx account
  const handleAddAccount = async () => {
    if (!selectedUser) return;
    if (!newAccountForm.email || !newAccountForm.password || !newAccountForm.label) {
      toast.error('Compila tutti i campi');
      return;
    }
    setSavingAccount(true);
    try {
      const response = await axios.post(`${API}/api/arbicsx/users/${selectedUser.id}/accounts`, newAccountForm);
      setArbicsxAccounts([...arbicsxAccounts, response.data.account]);
      setNewAccountForm({ email: '', password: '', label: '' });
      setShowNewAccountForm(false);
      toast.success('Profilo Arbicsx aggiunto');
    } catch (error) {
      toast.error('Errore nell\'aggiunta del profilo');
    } finally {
      setSavingAccount(false);
    }
  };

  // Delete Arbicsx account
  const handleDeleteAccount = async (accountId) => {
    if (!selectedUser) return;
    setDeletingAccountId(accountId);
    try {
      await axios.delete(`${API}/api/arbicsx/users/${selectedUser.id}/accounts/${accountId}`);
      setArbicsxAccounts(arbicsxAccounts.filter(acc => acc.id !== accountId));
      toast.success('Profilo Arbicsx eliminato');
    } catch (error) {
      toast.error('Errore nell\'eliminazione del profilo');
    } finally {
      setDeletingAccountId(null);
    }
  };

  // Toggle password visibility for a specific account
  const toggleAccountPasswordVisibility = (accountId) => {
    setShowAccountPasswords(prev => ({
      ...prev,
      [accountId]: !prev[accountId]
    }));
  };

  // Filter users by search query
  const filteredUsers = useMemo(() => {
    let result = users;
    
    // Filtra per query di ricerca
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter(user => 
        user.name?.toLowerCase().includes(query) || 
        user.email?.toLowerCase().includes(query)
      );
    }
    
    // Ordina alfabeticamente per nome (A→Z)
    return result.slice().sort((a, b) => 
      (a.name || '').localeCompare(b.name || '', 'it', { sensitivity: 'base' })
    );
  }, [users, searchQuery]);
  
  // Separa admin primari, admin secondari e utenti normali
  // Admin Secondary vede SOLO i propri utenti, Admin Primary vede tutti
  const ELIA_ID = "1bf7412e-e9ec-42bd-af49-3b3eeb7d3971";
  
  const { primaryAdmins, secondaryAdmins, regularUsers, myUsers, usersGroupedByAdmin } = useMemo(() => {
    // Per admin secondario: filtra solo i propri utenti
    const usersToShow = isAdminSecondary 
      ? filteredUsers.filter(u => u.admin_id === currentUser?.id || u.admin_id === currentUser?.user_id)
      : filteredUsers;
    
    const primary = usersToShow.filter(u => 
      u.role === 'admin_primary' || u.user_id === ELIA_ID
    );
    const secondary = usersToShow.filter(u => u.role === 'admin_secondary');
    const regular = usersToShow.filter(u => 
      u.role !== 'admin_primary' && 
      u.role !== 'admin_secondary' && 
      u.user_id !== ELIA_ID
    );
    
    // Per admin secondario, myUsers sono i suoi utenti diretti
    const my = isAdminSecondary ? regular : [];
    
    // Raggruppa utenti per admin (solo per admin primary)
    let grouped = {};
    if (!isAdminSecondary) {
      // Gruppo "I miei utenti" - utenti con admin_id = Elia o null/undefined
      const myDirectUsers = regular.filter(u => 
        !u.admin_id || u.admin_id === ELIA_ID
      );
      grouped['_my_users'] = {
        name: 'I miei utenti',
        users: myDirectUsers,
        isMyUsers: true
      };
      
      // Gruppi per ogni admin secondario
      secondary.forEach(admin => {
        const adminId = admin.user_id || admin.id;
        const adminUsers = regular.filter(u => u.admin_id === adminId);
        if (adminUsers.length > 0 || true) { // Mostra sempre la sezione anche se vuota
          grouped[adminId] = {
            name: `Utenti di ${admin.name}`,
            adminName: admin.name,
            users: adminUsers,
            isMyUsers: false
          };
        }
      });
    }
    
    return { 
      primaryAdmins: isAdminSecondary ? [] : primary,
      secondaryAdmins: isAdminSecondary ? [] : secondary,
      regularUsers: regular,
      myUsers: my,
      usersGroupedByAdmin: grouped
    };
  }, [filteredUsers, isAdminSecondary, currentUser]);

  // State per vedere utenti di un admin secondario
  const [viewingAdminUsers, setViewingAdminUsers] = useState(null);

  // Colori di gruppo (solo aspetto): verde all'admin principale, palette agli admin secondari
  const groupColorMap = useMemo(() => buildGroupColorMap(secondaryAdmins), [secondaryAdmins]);
  const myGroupColor = isAdminSecondary
    ? (groupColorMap[currentUser?.id] || groupColorMap[currentUser?.user_id] || GROUP_PALETTE[0])
    : PRIMARY_COLOR;

  // Azioni della scheda utente (logica identica a prima, spostata nel menu ⋯)
  const handleOpenNominate = (user) => {
    setNominateUserId(user.user_id || user.id);
    setNominateUserName(user.name);
    setShowNominateModal(true);
  };
  const handleOpenTransfer = (user) => {
    setTransferUserId(user.user_id || user.id);
    setTransferUserName(user.name);
    fetchAvailableAdmins();
    setShowTransferModal(true);
  };
  const handleToggleActiveUser = (user) => {
    const newStatus = user.is_active === false;
    fetch(`${API}/api/arbicsx/users/set-active-status`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      body: JSON.stringify({ user_id: user.id, is_active: newStatus })
    }).then(() => {
      setUsers(users.map(u => u.id === user.id ? { ...u, is_active: newStatus } : u));
    }).catch(err => console.error('Error updating active status:', err));
  };

  
  // State per sezioni collassabili (raggruppamento utenti per admin)
  const [collapsedSections, setCollapsedSections] = useState({});
  
  // State per nominare admin secondario
  const [showNominateModal, setShowNominateModal] = useState(false);
  const [nominateUserId, setNominateUserId] = useState(null);
  const [nominateUserName, setNominateUserName] = useState('');
  const [nominating, setNominating] = useState(false);
  
  // State per trasferire utente
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferUserId, setTransferUserId] = useState(null);
  const [transferUserName, setTransferUserName] = useState('');
  const [transferToAdmin, setTransferToAdmin] = useState('');
  const [transferring, setTransferring] = useState(false);
  const [availableAdmins, setAvailableAdmins] = useState([]);

  // Test Telegram notifications (admin only)
  const [testingNotifications, setTestingNotifications] = useState(false);
  const handleTestNotifications = async () => {
    setTestingNotifications(true);
    try {
      const response = await testTelegramNotifications();
      if (response.data.success) {
        toast.success(`${response.data.message} - Controlla Telegram!`);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel test notifiche');
    } finally {
      setTestingNotifications(false);
    }
  };

  // Nomina admin secondario (solo admin primary)
  const handleNominateAdmin = async () => {
    if (!nominateUserId) return;
    setNominating(true);
    try {
      await axios.post(`${API}/api/admin/nominate-secondary`, 
        { user_id: nominateUserId },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      toast.success(`${nominateUserName} è stato nominato Admin Secondario`);
      setShowNominateModal(false);
      setNominateUserId(null);
      setNominateUserName('');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella nomina');
    } finally {
      setNominating(false);
    }
  };

  // State per revocare admin secondario
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [revokeUserId, setRevokeUserId] = useState(null);
  const [revokeUserName, setRevokeUserName] = useState('');
  const [revoking, setRevoking] = useState(false);

  // Revoca admin secondario (solo admin primary)
  const handleRevokeAdmin = async () => {
    if (!revokeUserId) return;
    setRevoking(true);
    try {
      await axios.post(`${API}/api/admin/revoke-secondary`, 
        { user_id: revokeUserId },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      toast.success(`${revokeUserName} non è più Admin Secondario`);
      setShowRevokeModal(false);
      setRevokeUserId(null);
      setRevokeUserName('');
      setViewingAdminUsers(null); // Chiude la lista utenti espansa
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella revoca');
    } finally {
      setRevoking(false);
    }
  };

  // Fetch available admins per trasferimento
  const fetchAvailableAdmins = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/list-secondaries`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setAvailableAdmins(response.data.admins || []);
    } catch (error) {
      console.error('Error fetching admins:', error);
    }
  };

  // Trasferisci utente (solo admin primary)
  const handleTransferUser = async () => {
    if (!transferUserId || !transferToAdmin) return;
    setTransferring(true);
    try {
      await axios.post(`${API}/api/admin/transfer-user`, 
        { user_id: transferUserId, new_admin_id: transferToAdmin },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      toast.success(`${transferUserName} è stato trasferito`);
      setShowTransferModal(false);
      setTransferUserId(null);
      setTransferUserName('');
      setTransferToAdmin('');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel trasferimento');
    } finally {
      setTransferring(false);
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await getUsersWithSubscriptions();
      setUsers(response.data);
      if (selectedUser) {
        const fresh = response.data.find(u => u.id === selectedUser.id);
        if (fresh) setSelectedUser(prev => ({ ...prev, ...fresh }));
      }
    } catch (error) {
      // Fallback to old API if new one fails
      try {
        const fallback = await getAdminUsers();
        setUsers(fallback.data);
      } catch (e) {
        toast.error('Errore nel caricamento degli utenti');
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch aggregate summary for admin dashboard card
  const fetchAggregateSummary = async () => {
    if (!isAdmin) return;
    setLoadingAggregate(true);
    try {
      const response = await getAdminAggregateSummary();
      setAggregateSummary(response.data);
      setAggregateSelectedIds(response.data.profits?.selected_user_ids || []);
    } catch (error) {
      console.error('Error fetching aggregate summary:', error);
    } finally {
      setLoadingAggregate(false);
    }
  };

  // Toggle user selection for aggregate profits
  const toggleAggregateUser = async (userId) => {
    const newSelection = aggregateSelectedIds.includes(userId)
      ? aggregateSelectedIds.filter(id => id !== userId)
      : [...aggregateSelectedIds, userId];
    
    setAggregateSelectedIds(newSelection);
    
    try {
      await updateAdminAggregatePreferences({ selected_user_ids: newSelection });
      // Refresh summary to get updated profits
      fetchAggregateSummary();
    } catch (error) {
      toast.error('Errore nel salvataggio preferenze');
    }
  };

  // Select/deselect all users for aggregate
  const toggleAllAggregateUsers = async () => {
    const allUserIds = aggregateSummary?.all_users?.map(u => u.id) || [];
    const newSelection = aggregateSelectedIds.length === allUserIds.length ? [] : allUserIds;
    
    setAggregateSelectedIds(newSelection);
    
    try {
      await updateAdminAggregatePreferences({ selected_user_ids: newSelection });
      fetchAggregateSummary();
    } catch (error) {
      toast.error('Errore nel salvataggio preferenze');
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchAggregateSummary();
  }, []);

  const handleApprove = async (userId) => {
    try {
      await approveUser(userId);
      toast.success('Utente approvato');
      fetchUsers();
    } catch (error) {
      toast.error('Errore nell\'approvazione');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteUser(deleteUserId);
      toast.success('Utente e tutti i suoi dati eliminati');
      setShowDeleteDialog(false);
      setDeleteUserId(null);
      setDeleteUserName('');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione');
    }
  };

  const handleEnterProfile = (user) => {
    startImpersonation(user);
    navigate('/arbicsx/dashboard');
  };

  const handleGenerateResetCode = async (user) => {
    setGeneratingCode(true);
    try {
      const response = await generateResetCode(user.id);
      setResetCodeData({
        code: response.data.code,
        userName: response.data.user_name,
        expiresAt: response.data.expires_at
      });
      setShowResetCodeModal(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella generazione del codice');
    } finally {
      setGeneratingCode(false);
    }
  };

  const copyCodeToClipboard = () => {
    navigator.clipboard.writeText(resetCodeData.code);
    toast.success('Codice copiato negli appunti');
  };

  const handlePhotoClick = (userId) => {
    if (fileInputRefs.current[userId]) {
      fileInputRefs.current[userId].click();
    }
  };

  const handlePhotoChange = async (userId, event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Formato non supportato. Usa JPG, PNG o WebP');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File troppo grande. Massimo 5MB');
      return;
    }

    setUploadingPhotoFor(userId);
    try {
      await uploadUserPhoto(userId, file);
      toast.success('Foto caricata con successo');
      fetchUsers(); // Refresh to show new photo
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel caricamento della foto');
    } finally {
      setUploadingPhotoFor(null);
      // Reset input
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  // Subscription handlers
  const handleOpenRenew = (user) => {
    setRenewUserId(user.id);
    setRenewUserName(user.name);
    setRenewMonths(1);
    setRenewAmount('');
    setShowRenewModal(true);
  };

  const handleRenew = async () => {
    setRenewing(true);
    try {
      await renewSubscription(renewUserId, renewMonths, parseFloat(renewAmount) || 0);
      toast.success(`${renewMonths} mesi aggiunti a ${renewUserName}`);
      setShowRenewModal(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel rinnovo');
    } finally {
      setRenewing(false);
    }
  };

  const handleToggleFree = async (user) => {
    const newFreeStatus = !user.is_free;
    try {
      await setUserFree(user.id, newFreeStatus);
      toast.success(newFreeStatus ? `${user.name} ora è gratuito` : `${user.name} ora è pagante`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleToggleBlock = async (user) => {
    const newBlockStatus = !user.is_blocked;
    try {
      await blockUser(user.id, newBlockStatus);
      toast.success(newBlockStatus ? `${user.name} bloccato` : `${user.name} sbloccato`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleOpenPaymentHistory = async (user) => {
    setPaymentHistoryUser(user);
    setShowPaymentHistory(true);
    setLoadingHistory(true);
    try {
      const response = await getUserPaymentHistory(user.id);
      setPaymentHistory(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento storico');
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleOpenExpiry = (user) => {
    setExpiryUserId(user.id);
    setExpiryUserName(user.name);
    setNewExpiryDate(user.subscription_expires ? user.subscription_expires.split('T')[0] : '');
    setShowExpiryModal(true);
  };

  const handleSetExpiry = async () => {
    if (!newExpiryDate) {
      toast.error('Seleziona una data');
      return;
    }
    try {
      await setUserExpiry(expiryUserId, new Date(newExpiryDate).toISOString());
      toast.success(`Scadenza impostata per ${expiryUserName}`);
      setShowExpiryModal(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  // Trial period handlers
  const handleOpenTrial = (user) => {
    setTrialUserId(user.id);
    setTrialUserName(user.name);
    setTrialDays(15);
    setShowTrialModal(true);
  };

  const handleSetTrial = async () => {
    setSettingTrial(true);
    try {
      await setUserTrial(trialUserId, trialDays);
      toast.success(`Periodo di prova di ${trialDays} giorni avviato per ${trialUserName}`);
      setShowTrialModal(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    } finally {
      setSettingTrial(false);
    }
  };

  // Handle reset Telegram
  const handleResetTelegram = async () => {
    if (!resetTelegramUser) return;
    setResettingTelegram(true);
    try {
      await resetUserTelegram(resetTelegramUser.id);
      toast.success(`Telegram resettato per ${resetTelegramUser.name}`);
      setShowResetTelegramConfirm(false);
      setResetTelegramUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel reset Telegram');
    } finally {
      setResettingTelegram(false);
    }
  };
  
  // Open Reset Telegram confirmation
  const openResetTelegramConfirm = (user) => {
    setResetTelegramUser(user);
    setShowResetTelegramConfirm(true);
  };
  
  // Open Link Telegram modal
  const openLinkTelegramModal = (user) => {
    setLinkTelegramUser(user);
    setLinkTelegramChatId('');
    setShowLinkTelegramModal(true);
  };
  
  // Handle link Telegram
  const handleLinkTelegram = async () => {
    if (!linkTelegramUser || !linkTelegramChatId.trim()) return;
    setLinkingTelegram(true);
    try {
      await axios.put(`${API}/api/arbicsx/users/${linkTelegramUser.id}/telegram`, 
        { telegram_chat_id: linkTelegramChatId.trim() },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      toast.success(`Telegram collegato per ${linkTelegramUser.name}`);
      setShowLinkTelegramModal(false);
      setLinkTelegramUser(null);
      setLinkTelegramChatId('');
      fetchUsers();
      // Aggiorna anche selectedUser se è lo stesso utente
      if (selectedUser && selectedUser.id === linkTelegramUser.id) {
        setSelectedUser({ ...selectedUser, telegram_chat_id: linkTelegramChatId.trim() });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel collegamento Telegram');
    } finally {
      setLinkingTelegram(false);
    }
  };
  
  // Open End Trial confirmation
  const openEndTrialConfirm = (user) => {
    setEndTrialUserId(user.id);
    setEndTrialUserName(user.name);
    setShowEndTrialConfirm(true);
  };

  // Handle end trial
  const handleEndTrial = async () => {
    if (!endTrialUserId) return;
    setEndingTrial(true);
    try {
      await endUserTrial(endTrialUserId);
      toast.success(`Prova terminata per ${endTrialUserName}`);
      setShowEndTrialConfirm(false);
      setEndTrialUserId(null);
      setEndTrialUserName('');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella terminazione prova');
    } finally {
      setEndingTrial(false);
    }
  };

  // Check if user is in trial period
  const isUserInTrial = (user) => {
    if (!user.trial_end_date) return false;
    const trialEnd = new Date(user.trial_end_date);
    return trialEnd > new Date();
  };

  // Calculate subscription expiration categories
  const expirationCategories = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    
    const categories = {
      expired: [],      // Scaduti
      today: [],        // Scadono oggi
      in3Days: [],      // Scadono entro 3 giorni
      in7Days: []       // Scadono entro 7 giorni
    };
    
    users.forEach(user => {
      // Skip free users, blocked users, or users without subscription
      if (user.is_free || user.is_blocked) return;
      
      // Check subscription_expires
      const expiryDate = user.subscription_expires || user.trial_end_date;
      if (!expiryDate) return;
      
      const expiry = new Date(expiryDate);
      expiry.setHours(0, 0, 0, 0);
      
      const diffDays = Math.floor((expiry - now) / (1000 * 60 * 60 * 24));
      
      if (diffDays < 0) {
        categories.expired.push(user);
      } else if (diffDays === 0) {
        categories.today.push(user);
      } else if (diffDays <= 3) {
        categories.in3Days.push(user);
      } else if (diffDays <= 7) {
        categories.in7Days.push(user);
      }
    });
    
    return categories;
  }, [users]);

  const hasExpirationAlerts = expirationCategories.expired.length > 0 || 
    expirationCategories.today.length > 0 || 
    expirationCategories.in3Days.length > 0 || 
    expirationCategories.in7Days.length > 0;

  const getSubscriptionBadge = (user) => {
    if (user.is_blocked) {
      return <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-500/20 text-red-400 border border-red-500/30">Bloccato</span>;
    }
    if (user.is_suspended) {
      return <span className="px-2 py-0.5 rounded text-xs font-medium bg-orange-500/20 text-[#f0b45a] border border-orange-500/30">Sospeso</span>;
    }
    if (user.is_free) {
      return <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-500/20 text-gray-400 border border-gray-500/30">Gratuito</span>;
    }
    if (user.trial_end_date) {
      const trialEnd = new Date(user.trial_end_date);
      if (trialEnd > new Date()) {
        return <span className="px-2 py-0.5 rounded text-xs font-medium bg-[rgba(40,44,52,.6)] text-[#e6e9ee] border border-[#282c34]">In Prova</span>;
      }
    }
    
    const status = user.subscription_status;
    if (!status) return null;
    
    if (status.status === 'scaduto') {
      return <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-500/20 text-red-400 border border-red-500/30">Scaduto</span>;
    }
    if (status.status === 'in_scadenza') {
      return <span className="px-2 py-0.5 rounded text-xs font-medium bg-orange-500/20 text-[#f0b45a] border border-orange-500/30">In scadenza</span>;
    }
    if (status.status === 'attivo') {
      return <span className="px-2 py-0.5 rounded text-xs font-medium bg-[rgba(61,219,169,.14)] text-[#3ddba9] border border-[rgba(61,219,169,.4)]">Attivo</span>;
    }
    return null;
  };

  // Get border and background color based on user status (for expiration alerts)
  const getCardStyle = (user) => {
    if (user.is_blocked) return 'border-red-500/50 bg-red-950/20';
    if (user.is_suspended) return 'border-orange-500/50 bg-orange-950/20';
    if (user.is_free) return 'border-gray-500/50';
    
    // Check trial
    if (user.trial_end_date) {
      const trialEnd = new Date(user.trial_end_date);
      const now = new Date();
      if (trialEnd > now) {
        const daysLeft = Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24));
        if (daysLeft <= 1) return 'border-red-500/50 bg-red-950/20';
        if (daysLeft <= 7) return 'border-orange-500/50 bg-orange-950/20';
        return 'border-[#282c34]';
      }
    }
    
    const status = user.subscription_status;
    if (!status) return 'border-[#282c34]';
    
    // Check subscription expiration with visual alerts
    if (status.days_left !== undefined) {
      if (status.days_left <= 0) return 'border-red-500/50 bg-red-950/20'; // Scaduto o oggi
      if (status.days_left <= 1) return 'border-red-500/50 bg-red-950/20'; // Entro 1 giorno
      if (status.days_left <= 7) return 'border-orange-500/50 bg-orange-950/20'; // Entro 7 giorni
    }
    
    if (status.status === 'scaduto') return 'border-red-500/50 bg-red-950/20';
    if (status.status === 'in_scadenza') return 'border-orange-500/50 bg-orange-950/20';
    if (status.status === 'attivo') return 'border-[rgba(61,219,169,.5)]';
    
    return 'border-[#282c34]';
  };

  // Legacy function for compatibility
  const getBorderColor = (user) => getCardStyle(user);

  const getExpiryText = (user) => {
    if (user.is_blocked) return 'Accesso bloccato';
    if (user.is_suspended) return 'Temporaneamente sospeso';
    if (user.is_free) return 'Gratuito permanente';
    
    // Check trial
    if (user.trial_end_date) {
      const trialEnd = new Date(user.trial_end_date);
      const now = new Date();
      if (trialEnd > now) {
        const daysLeft = Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24));
        return `Prova: ${daysLeft} giorni rimanenti`;
      }
    }
    
    const status = user.subscription_status;
    if (!status || status.days_left === undefined) return '';
    
    if (status.days_left < 0) {
      return `Scaduto da ${Math.abs(status.days_left)} giorni`;
    }
    if (status.days_left === 0) {
      return 'Scade oggi';
    }
    return `Scade tra ${status.days_left} giorni`;
  };

  // Handle toggle user active/inactive (is_approved)
  const handleToggleUserActive = async (user) => {
    const newStatus = !user.is_approved;
    try {
      await toggleUserStatus(user.id, newStatus);
      toast.success(`${user.name} ${newStatus ? 'attivato' : 'disattivato'}`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  // Handle suspend/reactivate
  const handleToggleSuspend = async (user) => {
    const newSuspendStatus = !user.is_suspended;
    try {
      await suspendUser(user.id, newSuspendStatus);
      toast.success(newSuspendStatus ? `${user.name} sospeso` : `${user.name} riattivato`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  // Handle user request (approve/reject)
  const handleRequestAction = async (user, request, action) => {
    try {
      await handleUserRequest(user.id, request.id, action);
      toast.success(`Richiesta ${action === 'approve' ? 'approvata' : 'rifiutata'}`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  // Handle management type toggle
  const handleToggleManagementType = async (user) => {
    const newType = user.management_type === 'managed' ? 'autonomous' : 'managed';
    try {
      await setUserManagementType(user.id, newType);
      toast.success(`${user.name} impostato come ${newType === 'managed' ? 'Gestito' : 'Autonomo'}`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  // Handle delegation type toggle
  const handleToggleDelegationType = async (user) => {
    const newType = user.delegation_type === 'total' ? 'partial' : 'total';
    try {
      await setUserDelegationType(user.id, newType);
      toast.success(`Delega ${newType === 'total' ? 'Totale' : 'Parziale'} per ${user.name}`);
      fetchUsers();
      if (selectedUser && selectedUser.id === user.id) {
        setSelectedUser({ ...selectedUser, delegation_type: newType });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  // Open quick action modal
  const openQuickAction = (user, actionType) => {
    setQuickActionUser(user);
    setQuickActionType(actionType);
    setQuickActionAmount('');
    setQuickActionNumAccounts(1);
    setQuickActionMethod('crypto');
    setQuickActionWho('admin');
    setShowQuickActionModal(true);
  };

  // Send quick action message
  const handleSendQuickAction = async () => {
    if (!quickActionUser) return;
    
    if (!quickActionUser.telegram_chat_id) {
      toast.error('Telegram non collegato per questo utente');
      return;
    }

    if (quickActionType === 'withdrawal' && (!quickActionAmount || parseFloat(quickActionAmount) <= 0)) {
      toast.error('Inserisci un importo valido');
      return;
    }

    if (quickActionType === 'purchase' && (!quickActionNumAccounts || quickActionNumAccounts < 1)) {
      toast.error('Inserisci un numero di conti valido');
      return;
    }

    setSendingQuickAction(true);
    try {
      const response = await sendQuickActionMessage(
        quickActionUser.id,
        quickActionType,
        quickActionType === 'withdrawal' ? parseFloat(quickActionAmount) : null,
        quickActionType === 'purchase' ? quickActionNumAccounts : null,
        quickActionMethod,
        quickActionWho
      );
      toast.success(`✅ Messaggio inviato a ${quickActionUser.name}`);
      setShowQuickActionModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore invio messaggio');
    } finally {
      setSendingQuickAction(false);
    }
  };

  // Send broadcast message to group of users
  const handleSendBroadcast = async () => {
    if (!broadcastMessage.trim()) {
      toast.error('Inserisci un messaggio');
      return;
    }
    
    if (broadcastTarget === 'selected' && selectedUserIds.length === 0) {
      toast.error('Seleziona almeno un utente');
      return;
    }

    setSendingBroadcast(true);
    try {
      // If selected, pass user IDs as target
      const targetValue = broadcastTarget === 'selected' 
        ? { type: 'selected', user_ids: selectedUserIds }
        : broadcastTarget;
      
      const response = await sendBroadcastMessage(targetValue, broadcastMessage.trim());
      toast.success(`✅ Messaggio inviato a ${response.data.sent_count} utenti`);
      setShowBroadcastModal(false);
      setBroadcastMessage('');
      setBroadcastTarget('all');
      setSelectedUserIds([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore invio messaggio');
    } finally {
      setSendingBroadcast(false);
    }
  };

  const formatValue = (value) => {
    if (value === undefined || value === null || value === 0) return '$0';
    const formatted = formatCurrency(Math.abs(value));
    return value >= 0 ? `+${formatted}` : `-${formatted}`;
  };

  const getValueColor = (value) => {
    if (value === undefined || value === null || value === 0) return 'text-gray-400';
    return value >= 0 ? 'text-[#3ddba9]' : 'text-red-400';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 accent-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white font-heading">
            {isAdminSecondary ? 'I Miei Utenti' : 'Utenti'}
          </h1>
          <p className="text-disabled mt-1">
            {isAdminSecondary 
              ? 'Gestisci la tua rete di utenti' 
              : 'Arbicsx - Gestione utenti e abbonamenti'}
          </p>
        </div>
        <div className="grid grid-cols-2 gap-2 w-full sm:flex sm:w-auto">
          {/* Send Broadcast Message Button - Solo per admin primary */}
          {isAdminPrimary && (
            <Button 
              onClick={() => setShowBroadcastModal(true)}
              variant="outline" 
              className="ht-btn w-full sm:w-auto min-h-[40px]"
              data-testid="broadcast-message-btn"
            >
              <MessageCircle size={16} />
              Invia Messaggio
            </Button>
          )}
          {/* Test Notifications Button - Admin primary only */}
          {isAdminPrimary && (
            <Button 
              onClick={handleTestNotifications}
              variant="outline" 
              className="ht-btn w-full sm:w-auto min-h-[40px]"
              disabled={testingNotifications}
              data-testid="test-notifications-btn"
              title="Invia tutte le notifiche di test a Telegram"
            >
              <Bell size={16} className={testingNotifications ? 'animate-pulse' : ''} />
              {testingNotifications ? 'Invio...' : 'Testa Notifiche'}
            </Button>
          )}
          {/* Global Report Button - Solo per admin primary */}
          {isAdminPrimary && (
            <Button 
              onClick={openGlobalReportModal}
              variant="outline" 
              className="ht-btn ht-btn-primary w-full sm:w-auto min-h-[40px]"
              data-testid="global-report-btn"
              title="Genera PDF con tutti gli utenti"
            >
              <FileText size={16} />
              Report Globale
            </Button>
          )}
          <Button 
            onClick={fetchUsers} 
            variant="outline" 
            className="ht-btn w-full sm:w-auto min-h-[40px]"
            disabled={loading}
            data-testid="users-refresh-btn"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Aggiorna
          </Button>
        </div>
      </div>

      {/* Link di Invito - Solo per Admin Secondari */}
      {isAdminSecondary && currentUser && (
        <div className="ht-card bg-gradient-to-r from-yellow-900/20 to-amber-900/10 border border-yellow-500/30 rounded-xl p-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h3 className="font-medium text-[#f0b45a] flex items-center gap-2">
                <ExternalLink size={16} />
                Il tuo Link di Invito
              </h3>
              <p className="text-sm text-gray-400 mt-1">
                Condividi questo link per far registrare nuovi utenti nella tua rete
              </p>
            </div>
            <div className="flex items-center gap-2">
              <code className="bg-black/50 px-3 py-2 rounded-lg text-sm text-yellow-300 font-mono">
                hedgingtracker.com/register?ref={currentUser.user_id || currentUser.id}
              </code>
              <Button
                onClick={() => {
                  navigator.clipboard.writeText(`https://hedgingtracker.com/register?ref=${currentUser.user_id || currentUser.id}`);
                  toast.success('Link copiato negli appunti!');
                }}
                variant="outline"
                size="sm"
                className="border-yellow-500/30 text-[#f0b45a] hover:bg-yellow-500/20"
              >
                <Copy size={14} />
              </Button>
            </div>
          </div>
          <div className="mt-3 flex items-center gap-4 text-sm">
            <span className="text-gray-400">
              Utenti nella tua rete: <span className="text-[#f0b45a] font-semibold">{regularUsers.length}</span>
            </span>
          </div>
        </div>
      )}

      {/* 📊 RIEPILOGO AGGREGATO CARD - Solo per Admin */}
      {isAdmin && aggregateSummary && (
        <AggregateSummaryCard
          summary={aggregateSummary}
          onRefresh={fetchAggregateSummary}
          loading={loadingAggregate}
          profitsAction={
            <button
              onClick={() => setShowAggregateSelector(!showAggregateSelector)}
              className="ht-btn ht-btn-sm neon n2"
              data-testid="aggregate-users-selector"
            >
              <Users size={12} />
              Utenti inclusi · {aggregateSummary.profits?.selected_count || 0} {(aggregateSummary.profits?.selected_count || 0) === 1 ? 'selezionato' : 'selezionati'}
              {showAggregateSelector ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            </button>
          }
          profitsSelector={showAggregateSelector && (
            <div className="mb-3 bg-[#11161d] rounded-xl p-3 border border-[#282c34] max-h-48 overflow-y-auto scroll-neutral">
              <div className="flex items-center justify-between mb-2 pb-2 border-b border-[#282c34]">
                <span className="text-xs text-gray-400">Seleziona utenti da includere:</span>
                <Button
                  onClick={toggleAllAggregateUsers}
                  variant="ghost"
                  size="sm"
                  className="text-xs text-[#3ddba9] hover:text-[#3ddba9]/80"
                >
                  {aggregateSelectedIds.length === (aggregateSummary.all_users?.length || 0) 
                    ? 'Deseleziona tutti' 
                    : 'Seleziona tutti'}
                </Button>
              </div>
              <div className="space-y-1">
                {sortProfilesForDisplay(aggregateSummary.all_users).map(u => (
                  <label key={u.id} className="flex items-center gap-2 p-1 rounded hover:bg-gray-700/50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={aggregateSelectedIds.includes(u.id)}
                      onChange={() => toggleAggregateUser(u.id)}
                      className="rounded border-gray-600 bg-gray-700 usdt-checkbox focus:ring-usdt/50"
                    />
                    <span className="text-sm text-gray-300">{u.name}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          emptyProfits={
            <div className="text-center py-4">
              <p className="text-sm text-disabled">Seleziona utenti da monitorare</p>
              <Button
                onClick={() => setShowAggregateSelector(true)}
                variant="ghost"
                size="sm"
                className="mt-2 ht-btn ht-btn-sm"
              >
                <Users size={14} className="mr-1" />
                Configura
              </Button>
            </div>
          }
        />
      )}

      {/* Subscription Expiration Banner */}
      {hasExpirationAlerts && (
        <div className="ht-panel p-4" data-testid="expiration-banner">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle size={18} className="text-[#f0b45a]" />
            <h3 className="font-medium text-white">Abbonamenti in Scadenza</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {/* Scaduti (Rosso) */}
            {expirationCategories.expired.length > 0 && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-red-400">Scaduti</span>
                  <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded-full font-medium">
                    {expirationCategories.expired.length}
                  </span>
                </div>
                <div className="space-y-1 max-h-20 overflow-y-auto">
                  {expirationCategories.expired.map(u => (
                    <p key={u.id} className="text-xs text-red-300 truncate">{u.name}</p>
                  ))}
                </div>
              </div>
            )}
            
            {/* Scadono oggi (Arancione) */}
            {expirationCategories.today.length > 0 && (
              <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-[#f0b45a]">Scadono Oggi</span>
                  <span className="px-2 py-0.5 bg-orange-500/20 text-[#f0b45a] text-xs rounded-full font-medium">
                    {expirationCategories.today.length}
                  </span>
                </div>
                <div className="space-y-1 max-h-20 overflow-y-auto">
                  {expirationCategories.today.map(u => (
                    <p key={u.id} className="text-xs text-orange-300 truncate">{u.name}</p>
                  ))}
                </div>
              </div>
            )}
            
            {/* Scadono tra 3 giorni (Arancione) */}
            {expirationCategories.in3Days.length > 0 && (
              <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-[#f0b45a]">Entro 3 Giorni</span>
                  <span className="px-2 py-0.5 bg-orange-500/20 text-[#f0b45a] text-xs rounded-full font-medium">
                    {expirationCategories.in3Days.length}
                  </span>
                </div>
                <div className="space-y-1 max-h-20 overflow-y-auto">
                  {expirationCategories.in3Days.map(u => (
                    <p key={u.id} className="text-xs text-orange-300 truncate">{u.name}</p>
                  ))}
                </div>
              </div>
            )}
            
            {/* Scadono tra 7 giorni (Giallo) */}
            {expirationCategories.in7Days.length > 0 && (
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-[#f0b45a]">Entro 7 Giorni</span>
                  <span className="px-2 py-0.5 bg-yellow-500/20 text-[#f0b45a] text-xs rounded-full font-medium">
                    {expirationCategories.in7Days.length}
                  </span>
                </div>
                <div className="space-y-1 max-h-20 overflow-y-auto">
                  {expirationCategories.in7Days.map(u => (
                    <p key={u.id} className="text-xs text-yellow-300 truncate">{u.name}</p>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Search Bar */}
      <div className="mb-4">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-disabled" />
          <Input
            type="text"
            placeholder="Cerca utente per nome o email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="bg-[#0d1117] border-[#282c34] pl-10 pr-10"
            data-testid="user-search-input"
          />
          {searchQuery && (
            <button 
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white"
            >
              <X size={16} />
            </button>
          )}
        </div>
        {searchQuery && (
          <p className="text-xs text-disabled mt-1">
            {filteredUsers.length} utent{filteredUsers.length === 1 ? 'e' : 'i'} trovato/i
          </p>
        )}
      </div>

      {/* Users Grid - Divided by Admin / Active / Inactive */}
      
      {/* Admin Section - Primary + Secondary Admins - Solo per Admin Primary */}
      {isAdminPrimary && (primaryAdmins.length + secondaryAdmins.length) > 0 && (
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-[#3ddba9]"></span>
          Amministratori ({primaryAdmins.length + secondaryAdmins.length})
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {/* Primary Admin Cards (Blue) */}
          {primaryAdmins.map(adminUser => (
            <UserCard
              key={adminUser.user_id || adminUser.id}
              user={adminUser}
              accent={PRIMARY_COLOR}
              isAdmin={isAdmin}
              isAdminPrimary={false}
              onOpen={openUserDetails}
              onEnter={handleEnterProfile}
              onToggleActive={handleToggleActiveUser}
              activeFreePeriod={getActiveFreePeriod(adminUser)}
              formatDateDisplay={formatDateDisplay}
            />
          ))}
          
          {/* Secondary Admin Cards (Yellow) */}
          {secondaryAdmins.map(adminUser => {
            const adminId = adminUser.user_id || adminUser.id;
            const adminUserCount = regularUsers.filter(u => u.admin_id === adminId).length;
            return (
              <SecondaryAdminCard
                key={adminId}
                admin={adminUser}
                color={groupColorMap[adminId] || GROUP_PALETTE[0]}
                userCount={adminUserCount}
                viewing={viewingAdminUsers === adminId}
                onOpen={openUserDetails}
                onToggleUsers={() => setViewingAdminUsers(viewingAdminUsers === adminId ? null : adminId)}
                onRevoke={(a) => { setRevokeUserId(a.user_id || a.id); setRevokeUserName(a.name); setShowRevokeModal(true); }}
                onLinkTelegram={openLinkTelegramModal}
              />
            );
          })}
        </div>
        
        {/* Secondary Admin's Users List (expandable) */}
        {viewingAdminUsers && (
          <div className="mt-4 p-4 ht-panel">
            <h3 className="ht-panel-title text-sm mb-3">
              Utenti di {secondaryAdmins.find(a => a.user_id === viewingAdminUsers)?.name}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {regularUsers.filter(u => u.admin_id === viewingAdminUsers).map(user => (
                <div 
                  key={user.user_id}
                  onClick={() => openUserDetails(user)}
                  className="bg-[#0d1117] border border-yellow-500/20 rounded-lg p-2 cursor-pointer hover:bg-[#11161d] transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Avatar className="h-6 w-6 flex-shrink-0">
                      <AvatarFallback className="bg-gray-700 text-white text-xs">
                        {user.name?.[0]?.toUpperCase() || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-sm text-white truncate">{user.name}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      )}
      
      {/* Active Users Section - For Admin Primary: My users + compact cards for secondary admins */}
      {isAdminPrimary ? (
        <div className="mb-8 space-y-6">
          {/* Section: I miei utenti - Card complete originali */}
          <div>
            <h2 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-[#3ddba9]"></span>
              I miei utenti ({usersGroupedByAdmin['_my_users']?.users.filter(u => u.is_active !== false).length || 0})
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 stagger-in">
              {(usersGroupedByAdmin['_my_users']?.users || []).filter(u => u.is_active !== false).map(user => (
                <UserCard
                  key={user.id}
                  user={user}
                  accent={PRIMARY_COLOR}
                  isAdmin={isAdmin}
                  isAdminPrimary={isAdminPrimary}
                  onOpen={openUserDetails}
                  onEnter={handleEnterProfile}
                  onNominate={handleOpenNominate}
                  onTransfer={handleOpenTransfer}
                  onToggleActive={handleToggleActiveUser}
                  activeFreePeriod={getActiveFreePeriod(user)}
                  formatDateDisplay={formatDateDisplay}
                />
              ))}
            </div>
            {(usersGroupedByAdmin['_my_users']?.users || []).filter(u => u.is_active !== false).length === 0 && (
              <p className="text-sm text-disabled text-center py-4">Nessun utente diretto attivo</p>
            )}
          </div>

          {/* Compact Cards for Secondary Admins */}
          {secondaryAdmins.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
                Utenti degli Admin Secondari
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {secondaryAdmins.map(admin => {
                  const adminId = admin.user_id || admin.id;
                  const adminUsers = regularUsers.filter(u => u.admin_id === adminId && u.is_active !== false);
                  const isExpanded = expandedSections[`admin_${adminId}`];
                  
                  return (
                    <React.Fragment key={adminId}>
                      {/* Compact Card - clickable header */}
                      <button
                        onClick={() => toggleSection(`admin_${adminId}`)}
                        className="w-full bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 hover:bg-yellow-500/20 transition-colors text-left"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <FolderOpen size={20} className="text-[#8b919c]" />
                            <div>
                              <h3 className="font-medium text-white">Utenti di {admin.name}</h3>
                              <p className="text-sm text-[#f0b45a]">{adminUsers.length} utent{adminUsers.length === 1 ? 'e' : 'i'} attiv{adminUsers.length === 1 ? 'o' : 'i'}</p>
                            </div>
                          </div>
                          {isExpanded ? (
                            <ChevronDown size={20} className="text-[#f0b45a]" />
                          ) : (
                            <ChevronRight size={20} className="text-[#f0b45a]" />
                          )}
                        </div>
                      </button>
                      
                      {/* Expanded Users - Full width section */}
                      {isExpanded && (
                        <div className="col-span-full mt-3 mb-4 p-4 bg-[#0d1117]/30 rounded-lg border border-yellow-500/20">
                          <h4 className="text-sm font-medium text-[#f0b45a] mb-3">Utenti di {admin.name}</h4>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                            {adminUsers.length === 0 ? (
                              <div className="text-disabled text-sm italic py-2 col-span-full">
                                Nessun utente assegnato
                              </div>
                            ) : (
                              adminUsers.map(user => (
                                <UserCard
                                  key={user.id}
                                  user={user}
                                  accent={groupColorMap[adminId] || PRIMARY_COLOR}
                                  isAdmin={isAdmin}
                                  isAdminPrimary={isAdminPrimary}
                                  onOpen={openUserDetails}
                                  onEnter={handleEnterProfile}
                                  onNominate={handleOpenNominate}
                                  onTransfer={handleOpenTransfer}
                                  onToggleActive={handleToggleActiveUser}
                                  activeFreePeriod={getActiveFreePeriod(user)}
                                  formatDateDisplay={formatDateDisplay}
                                />
                              ))
                          )}
                          </div>
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      ) : (
      /* Active Users Section - Original for Admin Secondary */
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-[#3ddba9]"></span>
          Utenti Attivi ({regularUsers.filter(u => u.is_active !== false).length})
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 stagger-in">
          {regularUsers.filter(u => u.is_active !== false).map(user => (
            <UserCard
              key={user.id}
              user={user}
              accent={myGroupColor}
              isAdmin={isAdmin}
              isAdminPrimary={isAdminPrimary}
              onOpen={openUserDetails}
              onEnter={handleEnterProfile}
              onNominate={handleOpenNominate}
              onTransfer={handleOpenTransfer}
              onToggleActive={handleToggleActiveUser}
              activeFreePeriod={getActiveFreePeriod(user)}
              formatDateDisplay={formatDateDisplay}
            />
          ))}
        </div>
        {regularUsers.filter(u => u.is_active !== false).length === 0 && (
          <p className="text-sm text-disabled text-center py-4">Nessun utente attivo</p>
        )}
      </div>
      )}

      {/* Inactive Users Section */}
      {regularUsers.filter(u => u.is_active === false).length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-400 mb-3 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-gray-500"></span>
            Utenti Inattivi ({regularUsers.filter(u => u.is_active === false).length})
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 opacity-70">
            {regularUsers.filter(u => u.is_active === false).map(user => (
              <div 
                key={user.id} 
                onClick={() => openUserDetails(user)}
                className="bg-[#0d1117]/30 border border-[#282c34]/50 rounded-lg p-3 cursor-pointer hover:bg-[#11161d] transition-colors"
                data-testid={`user-card-${user.id}`}
              >
                {/* User Info Row */}
                <div className="flex items-center gap-2 mb-2">
                  <Avatar className="h-8 w-8 flex-shrink-0 opacity-60">
                    {user.profile_photo ? (
                      <AvatarImage src={user.profile_photo} alt={user.name} className="object-cover" />
                    ) : null}
                    <AvatarFallback className="bg-gray-700 text-gray-400 text-xs font-medium">
                      {user.name?.[0]?.toUpperCase() || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-medium text-gray-400 text-sm truncate">{user.name}</h3>
                  </div>
                </div>

                {/* Badges Row */}
                <div className="flex items-center gap-1.5 flex-wrap mb-2">
                  <span className="px-1.5 py-0.5 text-[10px] rounded font-medium bg-red-500/20 text-red-400">
                    Inattivo
                  </span>
                  <span className={`px-1.5 py-0.5 text-[10px] rounded font-medium ${
                    user.management_type === 'managed' 
                      ? 'bg-[rgba(139,92,246,.16)] text-[#b9a3f5]' 
                      : 'bg-[rgba(61,219,169,.14)] text-[#3ddba9]'
                  }`}>
                    {user.management_type === 'managed' ? 'Gestito' : 'Autonomo'}
                  </span>
                  {/* View Only Badge */}
                  {user.view_only && (
                    <span className="px-1.5 py-0.5 text-[10px] rounded font-medium bg-[rgba(240,180,90,.14)] text-[#f0b45a] flex items-center gap-0.5">
                      <EyeOff size={10} />
                      View Only
                    </span>
                  )}
                </div>

                {/* Stats Row */}
                <div className="flex items-center justify-between pt-2 border-t border-[#282c34]">
                  <div className="text-left">
                    <span className="text-[10px] text-disabled block">Profitto Totale</span>
                    <span className="text-xs font-medium text-disabled">
                      {formatCurrency(user.total_profits || 0)}
                    </span>
                  </div>
                  {user.compenso_attivo && (
                    <div className="text-right">
                      <span className="text-[10px] text-disabled block">Compenso Da Pagare</span>
                      <span className="text-xs font-medium text-disabled">
                        ${(user.compenso_da_pagare_usd || 0).toFixed(2)}
                      </span>
                    </div>
                  )}
                </div>
                
                {/* Free Period Active Badge - Inactive users */}
                {getActiveFreePeriod(user) && (
                  <div className="flex items-center gap-2 mt-2 py-1.5 px-2 bg-[rgba(61,219,169,.06)] rounded border border-[rgba(61,219,169,.18)]">
                    <Gift size={10} className="text-[#3ddba9]/60" />
                    <span className="text-[9px] text-[#3ddba9]/60">
                      Gratuito fino al {formatDateDisplay(getActiveFreePeriod(user).end_date)}
                    </span>
                  </div>
                )}
                
                {/* Wallet Balances Row - Inactive users (dimmed) */}
                {((user.wallet_balance_usdt || 0) > 0 || (user.wallet_balance_usdc || 0) > 0) && (
                  <div className="pt-2 border-t border-[#282c34]/50 space-y-1">
                    <div className="flex items-center gap-3">
                      {(user.wallet_balance_usdt || 0) > 0 && (
                        <span className="text-[10px] font-mono text-disabled">
                          USDT: ${(user.wallet_balance_usdt || 0).toFixed(2)}
                        </span>
                      )}
                      {(user.wallet_balance_usdc || 0) > 0 && (
                        <span className="text-[10px] font-mono text-disabled">
                          USDC: ${(user.wallet_balance_usdc || 0).toFixed(2)}
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] text-disabled">
                      Totale wallet: <span className="font-mono text-disabled">${((user.wallet_balance_usdt || 0) + (user.wallet_balance_usdc || 0)).toFixed(2)}</span>
                    </div>
                  </div>
                )}
                
                {/* Toggle Active Status Button - Admin only */}
                {isAdmin && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      const newStatus = user.is_active === false;
                      fetch(`${API}/api/arbicsx/users/set-active-status`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
                        body: JSON.stringify({ user_id: user.id, is_active: newStatus })
                      }).then(() => {
                        setUsers(users.map(u => u.id === user.id ? { ...u, is_active: newStatus } : u));
                      }).catch(err => console.error('Error updating active status:', err));
                    }}
                    className={`w-full mt-auto py-1.5 px-2 rounded text-[10px] font-medium transition-colors ${
                      user.is_active !== false
                        ? 'bg-transparent text-[#e86a78] hover:bg-red-500/15 border border-[rgba(232,106,120,.45)]'
                        : 'bg-[rgba(61,219,169,.14)] text-[#3ddba9] hover:bg-[rgba(61,219,169,.2)] border border-[rgba(61,219,169,.4)]'
                    }`}
                  >
                    {user.is_active !== false ? '🔴 Disattiva' : '🟢 Attiva'}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* User Details Modal */}
      <Dialog open={showUserDetails} onOpenChange={setShowUserDetails}>
        <DialogContent className="bg-[#0d1117] border-[#282c34] w-[calc(100vw-24px)] max-w-[900px] max-h-[90vh] overflow-y-auto overflow-x-hidden p-4 sm:p-5">
          {selectedUser && (
            <>
              <DialogHeader>
                <DialogTitle className="text-white w-full">
                  <div className="flex flex-col gap-3 w-full pr-10 sm:pr-0 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-start sm:items-center gap-3 min-w-0">
                    {/* Avatar with Photo Upload */}
                    <div className="relative flex-shrink-0 group">
                      <input
                        type="file"
                        ref={(el) => fileInputRefs.current[selectedUser.id] = el}
                        onChange={(e) => handlePhotoChange(selectedUser.id, e)}
                        accept="image/jpeg,image/png,image/webp"
                        className="hidden"
                      />
                      <Avatar 
                        className={`h-[52px] w-[52px] sm:h-14 sm:w-14 cursor-pointer transition-opacity ${uploadingPhotoFor === selectedUser.id ? 'opacity-50' : 'group-hover:opacity-80'}`}
                        onClick={() => handlePhotoClick(selectedUser.id)}
                      >
                        {selectedUser.profile_photo ? (
                          <AvatarImage src={selectedUser.profile_photo} alt={selectedUser.name} className="object-cover" />
                        ) : null}
                        <AvatarFallback className="bg-[#11161d] ht-pos text-lg font-medium">
                          {selectedUser.name?.[0]?.toUpperCase() || 'U'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                        <Camera size={18} className="text-white" />
                      </div>
                      {uploadingPhotoFor === selectedUser.id && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <RefreshCw size={18} className="text-white animate-spin" />
                        </div>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="break-words">{selectedUser.name}</span>
                        {/* Badge in linea solo da sm in su (desktop invariato) */}
                        <span className={`hidden sm:inline px-1.5 py-0.5 text-[10px] rounded font-medium ${
                          selectedUser.management_type === 'managed' 
                            ? 'bg-[rgba(139,92,246,.16)] text-[#b9a3f5]' 
                            : 'bg-[rgba(61,219,169,.14)] text-[#3ddba9]'
                        }`}>
                          {selectedUser.management_type === 'managed' ? 'Gestito' : 'Autonomo'}
                        </span>
                        {selectedUser.view_only && (
                          <span className="hidden sm:flex px-1.5 py-0.5 text-[10px] rounded font-medium bg-[rgba(240,180,90,.14)] text-[#f0b45a] items-center gap-1">
                            <EyeOff size={10} />
                            View Only
                          </span>
                        )}
                        <Send 
                          size={14} 
                          className={`flex-shrink-0 ${selectedUser.telegram_chat_id ? 'text-[#3ddba9]' : 'text-gray-600'}`}
                        />
                      </div>
                      {/* Badge sotto il nome su mobile */}
                      <div className="flex sm:hidden items-center gap-1.5 flex-wrap mt-1">
                        <span className={`px-1.5 py-0.5 text-[10px] rounded font-medium ${
                          selectedUser.management_type === 'managed' 
                            ? 'bg-[rgba(139,92,246,.16)] text-[#b9a3f5]' 
                            : 'bg-[rgba(61,219,169,.14)] text-[#3ddba9]'
                        }`}>
                          {selectedUser.management_type === 'managed' ? 'Gestito' : 'Autonomo'}
                        </span>
                        {selectedUser.view_only && (
                          <span className="px-1.5 py-0.5 text-[10px] rounded font-medium bg-[rgba(240,180,90,.14)] text-[#f0b45a] flex items-center gap-1">
                            <EyeOff size={10} />
                            View Only
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-400 font-normal truncate mt-0.5" title={selectedUser.email}>{selectedUser.email}</p>
                    </div>
                  </div>
                  {/* Azioni mobile: Entra grande + menu con le altre azioni */}
                  <div className="flex sm:hidden items-center gap-2">
                    <button
                      onClick={() => { handleEnterProfile(selectedUser); closeUserDetails(); }}
                      className="btn-magnetic btn-entra"
                      title="Entra"
                      aria-label={`Entra nel profilo di ${selectedUser.name}`}
                      data-magnetic
                      data-magnetic-x="4"
                      data-magnetic-y="4"
                      data-testid="user-enter-profile-btn"
                    >
                      <LogIn className="w-[18px] h-[18px] sm:w-4 sm:h-4 shrink-0" />
                      <span className="btn-entra-label">Entra</span>
                    </button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="outline"
                          className="h-[42px] w-[46px] p-0 flex-shrink-0 border-[#282c34] text-gray-300 hover:bg-[#11161d]"
                          title="Altre azioni"
                          data-testid="user-actions-menu-btn"
                        >
                          <MoreHorizontal size={20} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="ht-panel text-gray-200 z-[200]">
                        <DropdownMenuItem
                          onClick={() => handleGenerateResetCode(selectedUser)}
                          disabled={generatingCode}
                          className="gap-2 focus:bg-[#11161d] focus:text-white"
                          data-testid="user-reset-password-menu-item"
                        >
                          <Key size={16} className="text-[#f0b45a]" />
                          Reset Password
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => openReportModal(selectedUser)}
                          className="gap-2 focus:bg-[#11161d] focus:text-white"
                          data-testid="user-generate-report-menu-item"
                        >
                          <FileText size={16} className="text-[#3ddba9]" />
                          Genera Report PDF
                        </DropdownMenuItem>
                        <DropdownMenuSeparator className="bg-[#11161d]" />
                        <DropdownMenuItem
                          onClick={() => {
                            closeUserDetails();
                            setDeleteUserId(selectedUser.id);
                            setDeleteUserName(selectedUser.name);
                            setShowDeleteDialog(true);
                          }}
                          className="gap-2 text-red-400 focus:bg-red-500/10 focus:text-red-300"
                          data-testid="user-delete-menu-item"
                        >
                          <Trash2 size={16} />
                          Elimina Utente
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  {/* Quick Actions - Top Right - Large Buttons (desktop, invariate) */}
                  <div className="hidden sm:flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => { handleEnterProfile(selectedUser); closeUserDetails(); }}
                      className="btn-magnetic btn-entra"
                      title="Entra"
                      aria-label={`Entra nel profilo di ${selectedUser.name}`}
                      data-magnetic
                      data-magnetic-x="4"
                      data-magnetic-y="4"
                    >
                      <LogIn className="w-[18px] h-[18px] sm:w-4 sm:h-4 shrink-0" />
                      <span className="btn-entra-label">Entra</span>
                    </button>
                    <Button
                      onClick={() => handleGenerateResetCode(selectedUser)}
                      size="sm"
                      variant="outline"
                      className="h-10 px-3 text-sm gap-2 border-[rgba(240,180,90,.5)] text-[#f0b45a] hover:ht-warn hover:bg-[rgba(240,180,90,.08)]"
                      disabled={generatingCode}
                      title="Reset Password"
                    >
                      <Key size={18} />
                    </Button>
                    <Button
                      onClick={() => openReportModal(selectedUser)}
                      size="sm"
                      variant="outline"
                      className="h-10 px-3 text-sm gap-2 border-[rgba(61,219,169,.5)] text-[#3ddba9] hover:text-[#3ddba9] hover:bg-[rgba(61,219,169,.08)]"
                      title="Genera Report PDF"
                      data-testid="generate-report-btn"
                    >
                      <FileText size={18} />
                    </Button>
                    <Button
                      onClick={() => {
                        closeUserDetails();
                        setDeleteUserId(selectedUser.id);
                        setDeleteUserName(selectedUser.name);
                        setShowDeleteDialog(true);
                      }}
                      size="sm"
                      variant="outline"
                      className="h-10 px-3 text-sm gap-2 border-red-500/50 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      title="Elimina Utente"
                    >
                      <Trash2 size={18} />
                    </Button>
                  </div>
                  </div>
                </DialogTitle>
              </DialogHeader>

              {/* Pending Requests */}
              {selectedUser.pending_requests && selectedUser.pending_requests.length > 0 && (
                <div className="space-y-2 mb-4">
                  {selectedUser.pending_requests.map(req => (
                    <div 
                      key={req.id}
                      className={`flex items-center justify-between p-3 rounded-lg ${
                        req.request_type === 'renewal' 
                          ? 'bg-[rgba(61,219,169,.08)] border border-[rgba(61,219,169,.4)]' 
                          : 'bg-orange-500/10 border border-orange-500/30'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        {req.request_type === 'renewal' ? (
                          <CreditCard size={16} className="text-[#3ddba9]" />
                        ) : (
                          <Pause size={16} className="text-[#f0b45a]" />
                        )}
                        <span className={`text-sm font-medium ${
                          req.request_type === 'renewal' ? 'text-[#3ddba9]' : 'text-[#f0b45a]'
                        }`}>
                          Richiesta {req.request_type === 'renewal' ? 'Rinnovo' : 'Sospensione'}
                        </span>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleRequestAction(selectedUser, req, 'approve')}
                          size="sm"
                          className="h-8 px-3 ht-btn ht-btn-primary"
                        >
                          <CheckCircle size={14} className="mr-1" />
                          Approva
                        </Button>
                        <Button
                          onClick={() => handleRequestAction(selectedUser, req, 'reject')}
                          size="sm"
                          className="h-8 px-3 ht-btn ht-btn-danger"
                        >
                          <XCircle size={14} className="mr-1" />
                          Rifiuta
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* === 1D: due strisce di numeri sempre visibili === */}
              <div className="ht-panel px-3 py-2 mb-2">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4">
                  <div className="ht-row"><span className="ht-row-label">Profitto totale</span><span className={`ht-row-value ${getValueColor(selectedUser.stats?.total_profit)}`}>{formatValue(selectedUser.stats?.total_profit)}</span></div>
                  <div className="ht-row"><span className="ht-row-label">Oggi</span><span className={`ht-row-value ${getValueColor(selectedUser.stats?.today_profit)}`}>{formatValue(selectedUser.stats?.today_profit)}</span></div>
                  <div className="ht-row"><span className="ht-row-label">Settimanale</span><span className={`ht-row-value ${getValueColor(selectedUser.stats?.week_profit)}`}>{formatValue(selectedUser.stats?.week_profit)}</span></div>
                  <div className="ht-row"><span className="ht-row-label">Mensile</span><span className={`ht-row-value ${getValueColor(selectedUser.stats?.month_profit)}`}>{formatValue(selectedUser.stats?.month_profit)}</span></div>
                </div>
              </div>
              <div className="ht-panel px-3 py-2 mb-3">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4">
                  <div className="ht-row"><span className="ht-row-label">Conti attivi</span><span className="ht-row-value">{selectedUser.stats?.active_cycles || 0}</span></div>
                  <div className="ht-row"><span className="ht-row-label">In attesa</span><span className={`ht-row-value ${(selectedUser.stats?.awaiting_cycles || 0) > 0 ? 'ht-warn' : ''}`}>{selectedUser.stats?.awaiting_cycles || 0}</span></div>
                  <div
                    className="ht-row cursor-pointer"
                    onClick={() => {
                      if (!showCompletedCyclesPanel && selectedUser.stats?.completed_cycles > 0) {
                        loadCompletedCycles(selectedUser.id);
                      }
                      setShowCompletedCyclesPanel(!showCompletedCyclesPanel);
                    }}
                    data-testid="completed-cycles-toggle"
                  >
                    <span className="ht-row-label">Completati</span>
                    <span className="ht-row-value flex items-center gap-1 justify-end">
                      {selectedUser.stats?.completed_cycles || 0}
                      {selectedUser.stats?.completed_cycles > 0 && <Filter size={10} className={showCompletedCyclesPanel ? 'ht-pos' : 'ht-off'} />}
                    </span>
                  </div>
                  <div className="ht-row"><span className="ht-row-label">% mensile</span><span className={`ht-row-value ${(selectedUser.stats?.monthly_percent || 0) >= 0 ? 'ht-pos' : 'ht-alert'}`}>{selectedUser.stats?.monthly_percent !== undefined ? `${selectedUser.stats.monthly_percent}%` : '0%'}</span></div>
                </div>
              </div>

              {/* Completed Cycles Panel with Filters */}
              {showCompletedCyclesPanel && (
                <div className="bg-[#11161d]/40 rounded-lg p-3 mb-4 border border-[#282c34]/50">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-gray-300 flex items-center gap-2">
                      <History size={14} />
                      Cicli Completati
                    </span>
                    <button 
                      onClick={() => setShowCompletedCyclesPanel(false)}
                      className="text-gray-500 hover:text-white"
                    >
                      <X size={14} />
                    </button>
                  </div>

                  {/* Filters */}
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    {/* Prop Firm Filter */}
                    <select
                      value={cycleFilters.propFirm}
                      onChange={(e) => setCycleFilters({ ...cycleFilters, propFirm: e.target.value })}
                      className="bg-[#0d1117] border border-[#282c34] rounded px-2 py-1.5 text-xs text-gray-300"
                    >
                      <option value="">Tutte le Prop Firm</option>
                      {completedCyclesFilterOptions.prop_firms.map(pf => (
                        <option key={pf} value={pf}>{pf}</option>
                      ))}
                    </select>

                    {/* Month/Year Filter */}
                    <select
                      value={cycleFilters.monthYear}
                      onChange={(e) => setCycleFilters({ ...cycleFilters, monthYear: e.target.value })}
                      className="bg-[#0d1117] border border-[#282c34] rounded px-2 py-1.5 text-xs text-gray-300"
                    >
                      <option value="">Tutti i mesi</option>
                      {completedCyclesFilterOptions.month_years.map(my => (
                        <option key={my} value={my}>{my}</option>
                      ))}
                    </select>

                    {/* Type Filter */}
                    <select
                      value={cycleFilters.type}
                      onChange={(e) => setCycleFilters({ ...cycleFilters, type: e.target.value })}
                      className="bg-[#0d1117] border border-[#282c34] rounded px-2 py-1.5 text-xs text-gray-300"
                    >
                      <option value="">Tutti i tipi</option>
                      <option value="burned">Bruciato</option>
                      <option value="profit_prop">Profitto Prop</option>
                    </select>

                    {/* Result Filter */}
                    <select
                      value={cycleFilters.result}
                      onChange={(e) => setCycleFilters({ ...cycleFilters, result: e.target.value })}
                      className="bg-[#0d1117] border border-[#282c34] rounded px-2 py-1.5 text-xs text-gray-300"
                    >
                      <option value="">Tutti i risultati</option>
                      <option value="positive">Positivo (+)</option>
                      <option value="negative">Negativo (-)</option>
                    </select>
                  </div>

                  {/* Active Filters Summary */}
                  {(cycleFilters.propFirm || cycleFilters.monthYear || cycleFilters.type || cycleFilters.result) && (
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs text-disabled">Filtri attivi:</span>
                      <button 
                        onClick={() => setCycleFilters({ propFirm: '', monthYear: '', type: '', result: '' })}
                        className="text-xs text-[#e6e9ee] hover:text-[#8b919c]"
                      >
                        Rimuovi tutti
                      </button>
                    </div>
                  )}

                  {/* Cycles List */}
                  {loadingCompletedCycles ? (
                    <div className="text-center py-4">
                      <RefreshCw size={16} className="animate-spin text-disabled mx-auto" />
                    </div>
                  ) : filteredCompletedCycles.length > 0 ? (
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {filteredCompletedCycles.map(cycle => (
                        <div 
                          key={cycle.id} 
                          className={`bg-[#0d1117] rounded p-2 border ${
                            cycle.total_net_profit > 0 ? 'border-[rgba(61,219,169,.4)]' : 'border-red-500/30'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-medium text-white">{cycle.prop_firm}</span>
                              {(cycle.went_through_awaiting || cycle.payout_type) && (
                                <span className="px-1.5 py-0.5 text-[10px] bg-[rgba(240,180,90,.14)] text-[#f0b45a] rounded">
                                  Profitto Prop
                                </span>
                              )}
                            </div>
                            <span className={`text-xs font-mono ${
                              cycle.total_net_profit > 0 ? 'text-[#3ddba9]' : 'text-red-400'
                            }`}>
                              {cycle.total_net_profit > 0 ? '+' : ''}${cycle.total_net_profit?.toFixed(2) || '0.00'}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-[10px] text-disabled">
                              {cycle.end_date || cycle.start_date || 'N/A'}
                            </span>
                            {cycle.challenge_size && (
                              <span className="text-[10px] text-disabled">
                                ${cycle.challenge_size?.toLocaleString()}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-disabled text-center py-3">
                      {completedCycles.length > 0 ? 'Nessun ciclo corrisponde ai filtri' : 'Nessun ciclo completato'}
                    </p>
                  )}

                  {/* Summary */}
                  {filteredCompletedCycles.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-[#282c34]/50 flex justify-between text-xs">
                      <span className="text-disabled">{filteredCompletedCycles.length} cicli</span>
                      <span className={`font-mono ${
                        filteredCompletedCycles.reduce((sum, c) => sum + (c.total_net_profit || 0), 0) >= 0 
                          ? 'text-[#3ddba9]' 
                          : 'text-red-400'
                      }`}>
                        Totale: ${filteredCompletedCycles.reduce((sum, c) => sum + (c.total_net_profit || 0), 0).toFixed(2)}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* === 1D: menu laterale + un solo pannello alla volta === */}
              <div className="flex flex-col lg:flex-row gap-3">
                <aside className="grid grid-cols-4 gap-2 lg:flex lg:flex-col lg:w-[186px] lg:flex-shrink-0" data-testid="user-detail-menu">
                  {USER_PANELS.map(panel => {
                    const Icon = panel.icon;
                    const count = panel.count ? panel.count({ selectedUser, users, arbicsxAccounts }) : null;
                    const warn = panel.warn ? panel.warn({ selectedUser }) : false;
                    const active = activePanel === panel.id;
                    return (
                      <button
                        key={panel.id}
                        onClick={() => setActivePanel(panel.id)}
                        data-testid={`user-panel-${panel.id}`}
                        className={`ht-tile lg:hidden ${active ? 'ht-tile-active' : ''} ${warn ? 'cy' : ''}`}
                      >
                        <Icon size={15} />
                        <span>{panel.short}</span>
                        {count ? <span className="ht-tile-badge">{count}</span> : warn ? <span className="ht-tile-pin" /> : null}
                      </button>
                    );
                  })}
                  {USER_PANELS.map(panel => {
                    const Icon = panel.icon;
                    const count = panel.count ? panel.count({ selectedUser, users, arbicsxAccounts }) : null;
                    const warn = panel.warn ? panel.warn({ selectedUser }) : false;
                    const active = activePanel === panel.id;
                    return (
                      <button
                        key={`d-${panel.id}`}
                        onClick={() => setActivePanel(panel.id)}
                        data-testid={`user-panel-desktop-${panel.id}`}
                        className={`ht-side-item hidden lg:flex ${active ? 'ht-side-item-active' : ''} ${warn ? 'cy' : ''} ${active ? 'neon n2' : ''} ${active && warn ? 'cy' : ''}`}
                      >
                        <Icon size={14} />
                        <span className="leading-tight">{panel.label}</span>
                        {count ? <span className="ht-side-count">{count}</span> : warn ? <span className="ht-tile-pin" style={{ position: 'static', marginLeft: 'auto' }} /> : null}
                      </button>
                    );
                  })}
                </aside>
                <div className="flex-1 min-w-0" data-testid="user-detail-panel">
                  {activePanel === 'telegram' && (<>
              {/* Telegram Info */}
              <div className="ht-panel p-3 mb-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <Send size={14} className={`flex-shrink-0 ${selectedUser.telegram_chat_id ? 'text-[#3ddba9]' : 'text-disabled'}`} />
                    <span className="text-sm text-gray-400 break-words">
                      {selectedUser.telegram_chat_id ? `Telegram: ${selectedUser.telegram_chat_id}` : 'Telegram non collegato'}
                    </span>
                  </div>
                  {selectedUser.telegram_chat_id ? (
                    <Button
                      onClick={() => {
                        closeUserDetails();
                        openResetTelegramConfirm(selectedUser);
                      }}
                      size="sm"
                      variant="outline"
                      className="text-xs h-[34px] sm:h-7 flex-shrink-0 border-red-500/50 text-red-400 hover:bg-red-500/10"
                    >
                      <span className="sm:hidden">Reset</span>
                      <span className="hidden sm:inline">Reset Telegram</span>
                    </Button>
                  ) : (
                    <Button
                      onClick={() => {
                        closeUserDetails();
                        openLinkTelegramModal(selectedUser);
                      }}
                      size="sm"
                      variant="outline"
                      className="text-xs h-[34px] sm:h-7 flex-shrink-0 border-[#282c34] text-[#e6e9ee] hover:bg-[rgba(60,66,76,.6)]"
                    >
                      <span className="sm:hidden">Collega</span>
                      <span className="hidden sm:inline">Collega Telegram</span>
                    </Button>
                  )}
                </div>
              </div>
                  </>)}

                  {activePanel === 'collegamenti' && (<>
              {/* Profili collegati (solo utenti non admin) */}
              {!['admin', 'admin_primary', 'admin_secondary'].includes(selectedUser.role) && (
                <div className="ht-panel p-3 mb-3" data-testid="linked-profiles-section">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <Link2 size={14} className={`flex-shrink-0 ${selectedUser.profile_group_id ? 'text-[#3ddba9]' : 'text-disabled'}`} />
                      <span className="text-sm text-gray-400 break-words sm:truncate">
                        {selectedUser.profile_group_id
                          ? `${selectedUser.profile_label} · ${users.filter(u => u.profile_group_id === selectedUser.profile_group_id).length} profili collegati`
                          : 'Nessun profilo collegato'}
                      </span>
                    </div>
                    <Button
                      onClick={() => setShowProfileGroupModal(true)}
                      size="sm"
                      variant="outline"
                      className="text-xs h-[34px] sm:h-7 flex-shrink-0 border-[rgba(61,219,169,.5)] text-[#3ddba9] hover:bg-[rgba(61,219,169,.08)]"
                      data-testid="manage-profile-group-btn"
                    >
                      <span className="sm:hidden">Collega</span>
                      <span className="hidden sm:inline">Gestisci collegamento</span>
                    </Button>
                  </div>
                </div>
              )}

              {/* Famiglia (riepilogo condiviso) */}
              <div className="ht-panel p-3 mb-3" data-testid="family-section">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <Home size={14} className={`flex-shrink-0 ${selectedUser.family_group_id ? 'text-sky-400' : 'text-disabled'}`} />
                    <span className="text-sm text-gray-400 break-words sm:truncate">
                      {selectedUser.family_group_id
                        ? `Famiglia · ${users.filter(u => u.family_group_id === selectedUser.family_group_id).length} membri · vista ${selectedUser.family_view ? 'attiva' : 'spenta'}`
                        : 'Nessuna famiglia'}
                    </span>
                  </div>
                  <Button
                    onClick={() => setShowFamilyGroupModal(true)}
                    size="sm"
                    variant="outline"
                    className="text-xs h-[34px] sm:h-7 flex-shrink-0 border-sky-500/50 text-sky-400 hover:bg-sky-500/10"
                    data-testid="manage-family-group-btn"
                  >
                    <span className="sm:hidden">Famiglia</span>
                    <span className="hidden sm:inline">Gestisci famiglia</span>
                  </Button>
                </div>
              </div>
                  </>)}

                  {activePanel === 'impostazioni' && (<>
              {/* Toggle Section - Compatto */}
              <div className="ht-panel p-3 sm:p-4 mb-3">
                {/* Management Type Toggle */}
                <div className="flex items-center justify-between gap-2 py-2 px-3 bg-[#0d1117] rounded-lg">
                  <div className="flex items-center gap-2 min-w-0">
                    <Users size={14} className="text-disabled flex-shrink-0" />
                    <span className="text-xs text-gray-400">Tipo gestione</span>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 whitespace-nowrap">
                    <span className={`text-xs ${selectedUser.management_type === 'managed' ? 'text-disabled' : 'text-[#3ddba9] font-medium'}`}>
                      Autonomo
                    </span>
                    <Switch
                      checked={selectedUser.management_type === 'managed'}
                      onCheckedChange={() => {
                        handleToggleManagementType(selectedUser);
                        setSelectedUser({ ...selectedUser, management_type: selectedUser.management_type === 'managed' ? 'autonomous' : 'managed' });
                      }}
                      className="data-[state=checked]:bg-[#3ddba9]"
                    />
                    <span className={`text-xs ${selectedUser.management_type === 'managed' ? 'text-[#b9a3f5] font-medium' : 'text-disabled'}`}>
                      Gestito
                    </span>
                  </div>
                </div>

                {/* Active Status Toggle */}
                <div className="flex items-center justify-between gap-2 py-2 px-3 bg-[#0d1117] rounded-lg mt-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <Power size={14} className="text-disabled flex-shrink-0" />
                    <span className="text-xs text-gray-400">Stato utente</span>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 whitespace-nowrap">
                    <span className={`text-xs ${selectedUser.is_active !== false ? 'text-disabled' : 'text-red-400 font-medium'}`}>
                      Inattivo
                    </span>
                    <Switch
                      checked={selectedUser.is_active !== false}
                      onCheckedChange={async () => {
                        const newStatus = selectedUser.is_active === false;
                        try {
                          await fetch(`${API}/api/arbicsx/users/set-active-status`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
                            body: JSON.stringify({ user_id: selectedUser.id, is_active: newStatus })
                          });
                          setSelectedUser({ ...selectedUser, is_active: newStatus });
                          setUsers(users.map(u => u.id === selectedUser.id ? { ...u, is_active: newStatus } : u));
                        } catch (err) {
                          console.error('Error updating active status:', err);
                        }
                      }}
                      className="data-[state=checked]:bg-[#3ddba9]"
                    />
                    <span className={`text-xs ${selectedUser.is_active !== false ? 'text-[#3ddba9] font-medium' : 'text-disabled'}`}>
                      Attivo
                    </span>
                  </div>
                </div>

                {/* View Only Toggle - Solo Admin */}
                {isAdmin && (
                  <div className="flex items-center justify-between py-2 px-3 bg-[#0d1117] rounded-lg mt-2" data-testid="view-only-toggle">
                    <div className="flex items-center gap-2">
                      <EyeOff size={14} className="text-disabled" />
                      <span className="text-xs text-gray-400">Solo Visualizzazione</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs ${!selectedUser.view_only ? 'text-disabled' : 'text-[#f0b45a] font-medium'}`}>
                        Off
                      </span>
                      <Switch
                        checked={selectedUser.view_only === true}
                        onCheckedChange={async () => {
                          const newViewOnly = !selectedUser.view_only;
                          try {
                            await fetch(`${API}/api/arbicsx/users/set-view-only`, {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
                              body: JSON.stringify({ user_id: selectedUser.id, view_only: newViewOnly })
                            });
                            setSelectedUser({ ...selectedUser, view_only: newViewOnly });
                            setUsers(users.map(u => u.id === selectedUser.id ? { ...u, view_only: newViewOnly } : u));
                            toast.success(newViewOnly ? 'Modalità Solo Visualizzazione attivata' : 'Modalità Solo Visualizzazione disattivata');
                          } catch (err) {
                            console.error('Error updating view_only status:', err);
                            toast.error('Errore nel cambio stato');
                          }
                        }}
                        className="data-[state=checked]:bg-amber-600"
                      />
                      <span className={`text-xs ${selectedUser.view_only ? 'text-[#f0b45a] font-medium' : 'text-disabled'}`}>
                        On
                      </span>
                    </div>
                  </div>
                )}
              </div>
                  </>)}

                  {activePanel === 'profili' && (<>
              {/* Profili Arbicsx Section - Collapsible */}
              <div 
                className="ht-panel mb-3" 
                data-testid="profili-arbicsx-section"
              >
                <div 
                  className="flex items-center justify-between px-4 pt-3 pb-1"
                >
                  <div className="flex items-center gap-2">
                    <Key size={16} className="text-[#e6e9ee]" />
                    <span className="text-sm font-medium text-[#e6e9ee]">Profili Arbicsx</span>
                    {arbicsxAccounts.length > 0 && (
                      <span className="px-1.5 py-0.5 text-[10px] rounded bg-[rgba(40,44,52,.6)] text-[#e6e9ee] font-medium">
                        {arbicsxAccounts.length}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={(e) => { e.stopPropagation(); setShowNewAccountForm(!showNewAccountForm); setExpandedSections(prev => ({...prev, profiliArbicsx: true})); }}
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs gap-1 border-[#282c34] text-[#e6e9ee] hover:bg-[rgba(60,66,76,.6)]"
                    >
                      <Plus size={12} />
                      Nuovo
                    </Button>
                  </div>
                </div>
                
                {true && (
                  <div className="px-4 pb-4">
                    {loadingAccounts ? (
                      <div className="flex items-center justify-center py-4">
                        <RefreshCw size={16} className="animate-spin text-disabled" />
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {/* New Account Form */}
                        {showNewAccountForm && (
                          <div className="bg-[#0d1117] rounded-lg p-3 border border-cyan-500/30 space-y-2">
                            <div>
                              <Label className="text-xs text-disabled">Label (es. "Profilo 1")</Label>
                              <Input
                                type="text"
                                value={newAccountForm.label}
                                onChange={(e) => setNewAccountForm({...newAccountForm, label: e.target.value})}
                                placeholder="Profilo 1"
                                className="bg-[#11161d] border-[#282c34] text-white text-sm h-8 mt-1"
                              />
                            </div>
                            <div>
                              <Label className="text-xs text-disabled">Email</Label>
                              <Input
                                type="text"
                                value={newAccountForm.email}
                                onChange={(e) => setNewAccountForm({...newAccountForm, email: e.target.value})}
                                placeholder="email@arbicsx.com"
                                className="bg-[#11161d] border-[#282c34] text-white text-sm h-8 mt-1"
                              />
                            </div>
                            <div>
                              <Label className="text-xs text-disabled">Password</Label>
                              <Input
                                type="text"
                                value={newAccountForm.password}
                                onChange={(e) => setNewAccountForm({...newAccountForm, password: e.target.value})}
                                placeholder="password"
                                className="bg-[#11161d] border-[#282c34] text-white text-sm h-8 mt-1"
                              />
                            </div>
                            <div className="flex gap-2 mt-2">
                              <Button
                                onClick={handleAddAccount}
                                disabled={savingAccount}
                                size="sm"
                                className="flex-1 h-7 text-xs ht-btn ht-btn-primary text-white"
                              >
                                {savingAccount ? <RefreshCw size={12} className="animate-spin" /> : <Save size={12} />}
                                <span className="ml-1">Salva</span>
                              </Button>
                              <Button
                                onClick={() => { setShowNewAccountForm(false); setNewAccountForm({ email: '', password: '', label: '' }); }}
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs border-gray-600 text-gray-400"
                              >
                                Annulla
                              </Button>
                            </div>
                          </div>
                        )}
                    
                        {/* Existing Accounts List */}
                        {arbicsxAccounts.length === 0 && !showNewAccountForm ? (
                          <p className="text-xs text-disabled text-center py-2">Nessun profilo Arbicsx configurato</p>
                        ) : (
                          arbicsxAccounts.map((account) => (
                            <div key={account.id} className="bg-[#0d1117] rounded-lg p-3 border border-[#282c34]">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium text-white">{account.label}</span>
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-gray-400 bg-[#11161d] px-2 py-0.5 rounded">
                                    {account.ready_accounts} conti pronti
                                  </span>
                                  <button
                                    onClick={() => handleDeleteAccount(account.id)}
                                    disabled={deletingAccountId === account.id}
                                    className="text-red-400 hover:text-red-300 p-1"
                                  >
                                    {deletingAccountId === account.id ? (
                                      <RefreshCw size={14} className="animate-spin" />
                                    ) : (
                                      <Trash2 size={14} />
                                    )}
                                  </button>
                                </div>
                              </div>
                              <div className="space-y-2">
                                <div className="flex items-center gap-2 text-xs">
                                  <span className="text-disabled w-16">Email:</span>
                                  <span className="text-gray-300">{account.email}</span>
                                </div>
                                <div className="flex items-center gap-2 text-xs">
                                  <span className="text-disabled w-16">Password:</span>
                                  <span className="text-gray-300 font-mono">
                                    {showAccountPasswords[account.id] ? account.password : '••••••••'}
                                  </span>
                                  <button
                                    onClick={() => toggleAccountPasswordVisibility(account.id)}
                                    className="text-gray-500 hover:text-white"
                                  >
                                    {showAccountPasswords[account.id] ? <EyeOff size={12} /> : <Eye size={12} />}
                                  </button>
                                </div>
                                {/* Broker Account Number per profilo */}
                                <div className="flex items-center gap-2 text-xs pt-1 border-t border-[#282c34]/50 mt-2">
                                  <span className="text-[#f0b45a] w-16">N° Conto:</span>
                                  <input
                                    type="text"
                                    value={accountBrokerNumbers[account.id] || ''}
                                    onChange={(e) => setAccountBrokerNumbers(prev => ({ 
                                      ...prev, 
                                      [account.id]: e.target.value 
                                    }))}
                                    placeholder="Numero conto broker"
                                    className="bg-[#11161d] border border-gray-600 rounded px-2 py-1 text-xs text-white flex-1"
                                  />
                                  <button
                                    onClick={() => handleSaveAccountBroker(account.id)}
                                    disabled={savingAccountBroker[account.id]}
                                    className="bg-orange-600 hover:bg-orange-700 text-white px-2 py-1 rounded text-xs flex items-center gap-1"
                                  >
                                    {savingAccountBroker[account.id] ? (
                                      <RefreshCw size={10} className="animate-spin" />
                                    ) : (
                                      <Save size={10} />
                                    )}
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
                  </>)}

                  {activePanel === 'broker' && (<>
              {/* Broker Account Section */}
              <div 
                className="bg-[#11161d] border border-[#282c34] rounded-[12px] mb-4" 
                data-testid="broker-fp-section"
              >
                <div 
                  className="flex items-center justify-between p-4"
                >
                  <div className="flex items-center gap-2">
                    <CreditCard size={16} className="text-[#5f6672]" />
                    <span className="ht-panel-title text-sm">Broker & FP Trading</span>
                  </div>
                </div>
                
                {true && (
                  <div className="px-4 pb-4 space-y-4">
                    {/* FP Trading Credentials */}
                    <div>
                      <Label className="text-xs text-gray-400 mb-2 block">Credenziali FP Trading</Label>
                      <div className="space-y-2">
                        <div>
                          <Label className="text-xs text-disabled mb-1">Email</Label>
                          <Input
                            type="email"
                            value={fpEmail}
                            onChange={(e) => setFpEmail(e.target.value)}
                            placeholder="Email FP Trading"
                            className="bg-[#0d1117] border-[#282c34] text-white text-sm h-8"
                            data-testid="fp-email-input"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-disabled mb-1">Password</Label>
                          <div className="flex gap-2">
                            <Input
                              type={showFpPassword ? "text" : "password"}
                              value={fpPassword}
                              onChange={(e) => setFpPassword(e.target.value)}
                              placeholder="Password FP Trading"
                              className="bg-[#11161d] border-[#282c34] text-white text-sm h-8 flex-1"
                              data-testid="fp-password-input"
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => setShowFpPassword(!showFpPassword)}
                              className="h-8 px-2"
                            >
                              {showFpPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                            </Button>
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs text-disabled mb-1">PIN</Label>
                          <div className="flex gap-2">
                            <Input
                              type={showFpPin ? "text" : "password"}
                              value={fpPin}
                              onChange={(e) => setFpPin(e.target.value)}
                              placeholder="PIN FP Trading"
                              className="bg-[#11161d] border-[#282c34] text-white text-sm h-8 flex-1"
                              data-testid="fp-pin-input"
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => setShowFpPin(!showFpPin)}
                              className="h-8 px-2"
                            >
                              {showFpPin ? <EyeOff size={14} /> : <Eye size={14} />}
                            </Button>
                          </div>
                        </div>
                        <Button
                          onClick={handleSaveFpCredentials}
                          disabled={savingFpCredentials}
                          size="sm"
                          className="w-full ht-btn ht-btn-primary gap-1 mt-2"
                          data-testid="fp-credentials-save-btn"
                        >
                          {savingFpCredentials ? <RefreshCw size={12} className="animate-spin" /> : <Save size={12} />}
                          Salva Credenziali FP
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
                  </>)}

                  {activePanel === 'wallet' && (<>
              {/* Crypto Wallet Section */}
              <div 
                className="bg-[#11161d] border border-[#282c34] rounded-[12px] mb-4" 
                data-testid="crypto-wallet-section"
              >
                <div 
                  className="flex items-center justify-between p-4"
                >
                  <div className="flex items-center gap-2">
                    <Wallet size={16} className="text-[#3ddba9]" />
                    <span className="ht-panel-title text-sm">Crypto Wallet</span>
                  </div>
                </div>
                
                {true && (
                  <div className="px-4 pb-4">
                    <Label className="text-xs text-gray-400 mb-1 block">Indirizzo Wallet (ERC20)</Label>
                    <div className="flex gap-2">
                      <Input
                        type="text"
                        value={trustwalletAddress}
                        onChange={(e) => setTrustwalletAddress(e.target.value)}
                        placeholder="0x..."
                        className="bg-[#0d1117] border-[#282c34] text-white text-sm h-8 flex-1 font-mono text-xs"
                        data-testid="crypto-wallet-input"
                      />
                      <Button
                        onClick={handleSaveTrustwalletAddress}
                        disabled={savingTrustwalletAddress}
                        size="sm"
                        className="ht-btn ht-btn-sm ht-btn-primary"
                        data-testid="crypto-wallet-save-btn"
                      >
                        {savingTrustwalletAddress ? <RefreshCw size={12} className="animate-spin" /> : <Save size={12} />}
                        Salva
                      </Button>
                    </div>
                  </div>
                )}
              </div>
                  </>)}

                  {activePanel === 'compenso' && (<>
              {/* Compenso Gestore Section */}
              <div 
                className="bg-[#11161d] border border-[#282c34] rounded-[12px] mb-4" 
                data-testid="compenso-gestore-section"
              >
                <div 
                  className="flex items-center justify-between p-4"
                >
                  <div className="flex items-center gap-2">
                    <Percent size={16} className="text-[#5f6672]" />
                    <span className="ht-panel-title text-sm">Compenso Gestore</span>
                    {compensoData.compenso_attivo && (
                      <span className="ht-tag ht-tag-warn">
                        Attivo
                      </span>
                    )}
                    {/* Badge free period attivo */}
                    {compensoData.free_period_attivo && (
                      <span className="ht-tag ht-tag-pos flex items-center gap-1">
                        <Gift size={10} />
                        Gratuito
                      </span>
                    )}
                  </div>
                </div>
                
                {true && (
                  <div className="px-4 pb-4 space-y-4">
                    {loadingCompenso ? (
                      <div className="flex items-center justify-center py-4">
                        <RefreshCw size={16} className="animate-spin text-disabled" />
                      </div>
                    ) : (
                      <>
                        {/* Alert free period attivo */}
                        {compensoData.free_period_attivo && (
                          <div className="p-3 bg-[rgba(61,219,169,.08)] border border-[rgba(61,219,169,.4)] rounded-lg">
                            <div className="flex items-center gap-2 text-[#3ddba9] text-xs">
                              <Gift size={14} />
                              <span className="font-medium">
                                Gestione gratuita fino al {formatDateDisplay(compensoData.free_period_attivo.end_date)}
                              </span>
                            </div>
                            {compensoData.free_period_attivo.label && (
                              <p className="text-[#3ddba9]/70 text-[10px] mt-1 ml-5">
                                {compensoData.free_period_attivo.label}
                              </p>
                            )}
                          </div>
                        )}

                        {/* Toggle Attiva/Disattiva */}
                        <div className="flex items-center justify-between py-2 px-3 bg-[#0d1117] rounded-lg">
                          <span className="text-xs text-gray-400">Compenso attivo</span>
                          <Switch
                            checked={compensoData.compenso_attivo}
                            onCheckedChange={(checked) => setCompensoData({...compensoData, compenso_attivo: checked})}
                            className="data-[state=checked]:bg-amber-600"
                          />
                        </div>

                        {/* Data Inizio */}
                        <div>
                          <Label className="text-xs text-gray-400 mb-1 block">Data Inizio</Label>
                          <Input
                            type="date"
                            value={compensoData.compenso_data_inizio || ''}
                            onChange={(e) => setCompensoData({...compensoData, compenso_data_inizio: e.target.value})}
                            className="bg-[#0d1117] border-[#282c34] text-white text-sm h-8"
                          />
                        </div>

                        {/* Percentuale */}
                        <div>
                          <Label className="text-xs text-gray-400 mb-1 block">Percentuale (%)</Label>
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            step="0.1"
                            value={compensoData.compenso_percentuale}
                            onChange={(e) => setCompensoData({...compensoData, compenso_percentuale: parseFloat(e.target.value) || 0})}
                            placeholder="es. 20"
                            className="bg-[#0d1117] border-[#282c34] text-white text-sm h-8"
                          />
                        </div>

                        {/* Franchigia (solo visualizzazione) */}
                        <div className="flex items-center justify-between py-2 px-3 bg-[#0d1117] rounded-lg">
                          <span className="text-xs text-gray-400">Soglia Franchigia</span>
                          <span className="text-xs text-[#f0b45a] font-medium">€250,00</span>
                        </div>

                        {/* Salva Settings */}
                        <Button
                          onClick={handleSaveCompensoSettings}
                          disabled={savingCompenso}
                          size="sm"
                          className="w-full ht-btn ht-btn-primary gap-1"
                        >
                          {savingCompenso ? <RefreshCw size={12} className="animate-spin" /> : <Save size={12} />}
                          Salva Impostazioni
                        </Button>

                        {/* Data Reset Profitti */}
                        <div className="border-t border-[#282c34] pt-4">
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-xs text-gray-400 font-medium flex items-center gap-1">
                              <Calendar size={12} className="text-[#e6e9ee]" />
                              Data Reset Profitti
                            </span>
                          </div>
                          <div className="space-y-2 p-3 bg-[#0d1117] rounded-lg">
                            <p className="text-[10px] text-disabled mb-2">
                              I profitti verranno calcolati solo da questa data in poi
                            </p>
                            <div className="flex gap-2">
                              <Input
                                type="date"
                                value={selectedUser?.profit_reset_date || ''}
                                onChange={(e) => {
                                  setSelectedUser({...selectedUser, profit_reset_date: e.target.value || null});
                                }}
                                className="bg-[#11161d] border-[#282c34] text-white text-xs h-8 flex-1"
                              />
                              <Button
                                onClick={() => handleSetProfitResetDate(selectedUser?.id, selectedUser?.profit_reset_date)}
                                disabled={savingProfitReset}
                                size="sm"
                                className="h-8 text-xs ht-btn ht-btn-primary"
                              >
                                {savingProfitReset ? <RefreshCw size={12} className="animate-spin" /> : <Save size={12} />}
                              </Button>
                              {selectedUser?.profit_reset_date && (
                                <Button
                                  onClick={() => handleSetProfitResetDate(selectedUser?.id, null)}
                                  disabled={savingProfitReset}
                                  size="sm"
                                  variant="ghost"
                                  className="h-8 w-8 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                >
                                  <Trash2 size={12} />
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Periodi Gratuiti Manuali */}
                        <div className="border-t border-[#282c34] pt-4">
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-xs text-gray-400 font-medium flex items-center gap-1">
                              <Gift size={12} className="text-[#3ddba9]" />
                              Periodi Gratuiti Manuali
                            </span>
                          </div>
                          
                          {/* Lista periodi esistenti */}
                          {compensoData.free_periods && compensoData.free_periods.length > 0 && (
                            <div className="space-y-2 mb-3">
                              {compensoData.free_periods.map((fp, idx) => (
                                <div key={idx} className="flex items-center justify-between py-2 px-3 bg-[rgba(61,219,169,.08)] border border-usdt/20 rounded-lg">
                                  <div className="flex-1">
                                    <div className="text-xs text-[#3ddba9] font-medium">
                                      {formatDateDisplay(fp.start_date)} → {formatDateDisplay(fp.end_date)}
                                    </div>
                                    {fp.label && (
                                      <div className="text-[10px] text-[#3ddba9]/60 mt-0.5">{fp.label}</div>
                                    )}
                                  </div>
                                  <Button
                                    onClick={() => handleRemoveFreePeriod(fp.start_date)}
                                    disabled={removingFreePeriod === fp.start_date}
                                    size="sm"
                                    variant="ghost"
                                    className="h-6 w-6 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                  >
                                    {removingFreePeriod === fp.start_date ? (
                                      <RefreshCw size={12} className="animate-spin" />
                                    ) : (
                                      <Trash2 size={12} />
                                    )}
                                  </Button>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {/* Form per aggiungere nuovo periodo */}
                          <div className="space-y-2 p-3 bg-[#0d1117] rounded-lg">
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <Label className="text-[10px] text-disabled mb-1 block">Data Inizio</Label>
                                <Input
                                  type="date"
                                  value={newFreePeriod.start_date}
                                  onChange={(e) => setNewFreePeriod({...newFreePeriod, start_date: e.target.value})}
                                  className="bg-[#11161d] border-[#282c34] text-white text-xs h-7"
                                />
                              </div>
                              <div>
                                <Label className="text-[10px] text-disabled mb-1 block">Data Fine</Label>
                                <Input
                                  type="date"
                                  value={newFreePeriod.end_date}
                                  onChange={(e) => setNewFreePeriod({...newFreePeriod, end_date: e.target.value})}
                                  className="bg-[#11161d] border-[#282c34] text-white text-xs h-7"
                                />
                              </div>
                            </div>
                            <div>
                              <Label className="text-[10px] text-disabled mb-1 block">Note (opzionale)</Label>
                              <Input
                                type="text"
                                value={newFreePeriod.label}
                                onChange={(e) => setNewFreePeriod({...newFreePeriod, label: e.target.value})}
                                placeholder="es. Promo lancio"
                                className="bg-[#11161d] border-[#282c34] text-white text-xs h-7"
                              />
                            </div>
                            <Button
                              onClick={handleAddFreePeriod}
                              disabled={addingFreePeriod || !newFreePeriod.start_date || !newFreePeriod.end_date}
                              size="sm"
                              variant="outline"
                              className="w-full h-7 text-[10px] border-[rgba(61,219,169,.5)] text-[#3ddba9] hover:bg-[rgba(61,219,169,.08)] gap-1"
                            >
                              {addingFreePeriod ? <RefreshCw size={10} className="animate-spin" /> : <Plus size={10} />}
                              Aggiungi Periodo Gratuito
                            </Button>
                          </div>
                        </div>

                        {/* Riepilogo (read-only) */}
                        <div className="border-t border-[#282c34] pt-4 space-y-2">
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-disabled">Profitti soggetti a compenso</span>
                            <span className="text-white font-medium">{compensoData.profitti_soggetti_count || compensoData.profitti_soggetti || 0}</span>
                          </div>
                          {compensoData.profitti_esclusi_count > 0 && (
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-disabled">Profitti esclusi (periodi gratuiti)</span>
                              <span className="text-[#3ddba9] font-medium">{compensoData.profitti_esclusi_count} (${compensoData.profitti_esclusi_usd?.toFixed(2)})</span>
                            </div>
                          )}
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-disabled">Compenso maturato</span>
                            <span className="text-[#f0b45a] font-medium">
                              €{compensoData.compenso_maturato_eur?.toFixed(2)} / ${compensoData.compenso_maturato_usd?.toFixed(2)}
                            </span>
                          </div>
                          
                          {/* Compenso già pagato - Espandibile */}
                          <div className="border-t border-[#282c34] pt-2 mt-2">
                            <button
                              onClick={() => {
                                if (!showPagamentiList && selectedUser) {
                                  loadCompensoPagamenti(selectedUser.id);
                                }
                                setShowPagamentiList(!showPagamentiList);
                              }}
                              className="flex items-center justify-between text-xs w-full hover:bg-[#11161d] rounded p-1 -m-1"
                            >
                              <span className="text-disabled flex items-center gap-1">
                                Compenso già pagato
                                {showPagamentiList ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                              </span>
                              <span className="text-[#3ddba9] font-medium">
                                ${compensoData.compenso_pagato_usd?.toFixed(2) || compensoData.compenso_pagato_totale?.toFixed(2) || '0.00'}
                              </span>
                            </button>
                            
                            {/* Lista pagamenti espandibile */}
                            {showPagamentiList && (
                              <div className="mt-2 space-y-2">
                                {loadingPagamenti ? (
                                  <div className="flex justify-center py-2">
                                    <RefreshCw size={12} className="animate-spin text-disabled" />
                                  </div>
                                ) : compensoPagamenti.length === 0 ? (
                                  <p className="text-[10px] text-disabled text-center py-2">Nessun pagamento registrato</p>
                                ) : (
                                  compensoPagamenti.map((payment, idx) => (
                                    <div key={idx} className="p-2 bg-[#0d1117] rounded-lg border border-[#282c34]/50">
                                      <div className="flex items-center justify-between">
                                        <div>
                                          <span className="text-xs text-white font-medium">${payment.amount_usd?.toFixed(2)}</span>
                                          {payment.token && (
                                            <span className="text-[10px] text-disabled ml-1">({payment.token})</span>
                                          )}
                                        </div>
                                        <span className="text-[10px] text-disabled">{formatDateDisplay(payment.date)}</span>
                                      </div>
                                      {payment.tx_hash && (
                                        <a 
                                          href={getEtherscanLink(payment.tx_hash)}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="flex items-center gap-1 text-[10px] text-[#e6e9ee] hover:text-[#8b919c] mt-1"
                                        >
                                          <ExternalLink size={10} />
                                          {payment.tx_hash.slice(0, 10)}...{payment.tx_hash.slice(-8)}
                                        </a>
                                      )}
                                      {payment.notes && (
                                        <p className="text-[10px] text-disabled mt-1">{payment.notes}</p>
                                      )}
                                    </div>
                                  ))
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Bottone Richiedi Compenso */}
                        {compensoData.compenso_attivo && compensoData.compenso_maturato_eur > 0 && (
                          <Button
                            onClick={handleRichiestaCompenso}
                            size="sm"
                            variant="outline"
                            className="w-full h-8 text-xs border-[rgba(240,180,90,.5)] text-[#f0b45a] hover:bg-[rgba(240,180,90,.08)] gap-1"
                          >
                            <DollarSign size={12} />
                            Richiedi Compenso
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
                  </>)}
                </div>
              </div>

              {/* Main Actions */}
              <DialogFooter className="flex flex-wrap gap-2 sm:justify-start">
                {!selectedUser.is_approved && (
                  <Button
                    onClick={() => { handleApprove(selectedUser.id); closeUserDetails(); }}
                    size="sm"
                    className="gap-1 ht-btn ht-btn-primary"
                  >
                    <Check size={14} />
                    Approva
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="ht-panel text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Eliminare questo utente?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              Eliminando <strong className="text-white">{deleteUserName}</strong> verranno cancellati tutti i suoi dati in modo permanente (conti, profitti, costi, pagamenti, ecc.). 
              <br /><br />
              <span className="text-red-400">Questa azione non è reversibile.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="ht-btn border-0">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="ht-btn ht-btn-danger">
              Elimina utente
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset Code Modal */}
      <Dialog open={showResetCodeModal} onOpenChange={setShowResetCodeModal}>
        <DialogContent className="ht-panel max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Key className="text-[#f0b45a]" size={20} />
              Codice Reset Password
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-gray-400 text-sm">
              Codice reset per <strong className="text-white">{resetCodeData.userName}</strong>
            </p>
            
            <div className="bg-[rgba(240,180,90,.08)] border border-[rgba(240,180,90,.4)] rounded-lg p-4">
              <div className="flex items-center justify-between gap-3">
                <code className="text-2xl font-mono font-bold text-[#f0b45a] tracking-wider">
                  {resetCodeData.code}
                </code>
                <Button
                  onClick={copyCodeToClipboard}
                  size="sm"
                  variant="outline"
                  className="border-[rgba(240,180,90,.5)] text-[#f0b45a] hover:bg-[rgba(240,180,90,.08)]"
                >
                  <Copy size={16} />
                </Button>
              </div>
            </div>

            <div className="text-xs text-disabled space-y-1">
              <p>• Valido per <strong className="text-gray-300">24 ore</strong></p>
              <p>• Comunica questo codice all'utente (WhatsApp, Telegram, ecc.)</p>
              <p>• L'utente lo userà nella pagina di login</p>
              <p>• Un nuovo click invaliderà questo codice</p>
            </div>
          </div>
          <DialogFooter>
            <Button 
              onClick={() => setShowResetCodeModal(false)}
              className="ht-btn ht-btn-primary"
            >
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Renew Subscription Modal */}
      <Dialog open={showRenewModal} onOpenChange={setShowRenewModal}>
        <DialogContent className="ht-panel max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Plus className="text-[#3ddba9]" size={20} />
              Rinnova Abbonamento
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-gray-400 text-sm">
              Aggiungi mesi all'abbonamento di <strong className="text-white">{renewUserName}</strong>
            </p>
            
            <div className="space-y-2">
              <Label className="text-gray-400 text-sm">Durata</Label>
              <div className="flex flex-wrap gap-2">
                {[1, 3, 6, 12].map(m => (
                  <button
                    key={m}
                    onClick={() => setRenewMonths(m)}
                    className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                      renewMonths === m 
                        ? 'bg-[rgba(61,219,169,.14)] border-[rgba(61,219,169,.5)] text-[#3ddba9]' 
                        : 'border-[#282c34] text-gray-400 hover:border-gray-600'
                    }`}
                  >
                    {m} {m === 1 ? 'mese' : 'mesi'}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2 mt-2">
                <span className="text-disabled text-sm">Oppure:</span>
                <Input
                  type="number"
                  min="1"
                  placeholder="Mesi custom"
                  value={renewMonths > 12 ? renewMonths : ''}
                  onChange={(e) => setRenewMonths(parseInt(e.target.value) || 1)}
                  className="w-28 bg-black border-[#282c34]"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-400 text-sm">Importo pagato (opzionale)</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                placeholder="€ 0.00"
                value={renewAmount}
                onChange={(e) => setRenewAmount(e.target.value)}
                className="bg-black border-[#282c34]"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowRenewModal(false)} className="border-[#282c34]">
              Annulla
            </Button>
            <Button onClick={handleRenew} disabled={renewing} className="ht-btn ht-btn-primary">
              {renewing ? 'Aggiunta...' : `Aggiungi ${renewMonths} mesi`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment History Modal */}
      <Dialog open={showPaymentHistory} onOpenChange={setShowPaymentHistory}>
        <DialogContent className="ht-panel max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <History className="text-[#e6e9ee]" size={20} />
              Storico Pagamenti - {paymentHistoryUser?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {loadingHistory ? (
              <div className="flex justify-center py-8">
                <RefreshCw size={24} className="animate-spin text-disabled" />
              </div>
            ) : paymentHistory.payments.length === 0 ? (
              <p className="text-disabled text-center py-8">Nessun pagamento registrato</p>
            ) : (
              <div className="space-y-3 max-h-[300px] overflow-y-auto">
                {paymentHistory.payments.map((p, i) => (
                  <div key={i} className="bg-[#11161d] border border-[#282c34] rounded-lg p-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-sm font-medium text-white">{p.months} mesi</p>
                        <p className="text-xs text-disabled">{new Date(p.date).toLocaleDateString('it-IT')}</p>
                        {p.type === 'gift_code' && (
                          <span className="text-xs text-[#f0b45a]">Codice: {p.gift_code}</span>
                        )}
                      </div>
                      <span className="text-sm font-mono text-[#3ddba9]">
                        {p.amount > 0 ? `€${p.amount.toFixed(2)}` : 'Gratuito'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {paymentHistory.total_paid > 0 && (
              <div className="mt-4 pt-4 border-t border-[#282c34] flex justify-between">
                <span className="text-gray-400">Totale pagato:</span>
                <span className="text-lg font-mono font-bold text-[#3ddba9]">€{paymentHistory.total_paid.toFixed(2)}</span>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Set Expiry Modal */}
      <Dialog open={showExpiryModal} onOpenChange={setShowExpiryModal}>
        <DialogContent className="ht-panel max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Calendar className="text-[#e6e9ee]" size={20} />
              Imposta Scadenza
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-gray-400 text-sm">
              Imposta la data di scadenza per <strong className="text-white">{expiryUserName}</strong>
            </p>
            <Input
              type="date"
              value={newExpiryDate}
              onChange={(e) => setNewExpiryDate(e.target.value)}
              className="bg-black border-[#282c34]"
            />
            <p className="text-xs text-disabled">
              Questo rimuoverà anche lo stato "gratuito" se presente.
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowExpiryModal(false)} className="border-[#282c34]">
              Annulla
            </Button>
            <Button onClick={handleSetExpiry} className="ht-btn ht-btn-primary">
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Trial Period Modal */}
      <Dialog open={showTrialModal} onOpenChange={setShowTrialModal}>
        <DialogContent className="bg-[#0d0d0d] border-[#282c34] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Timer className="text-[#e6e9ee]" size={20} />
              Avvia Periodo di Prova
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-gray-400">
              Avvia un periodo di prova per <span className="text-white font-medium">{trialUserName}</span>.
              L'utente riceverà una notifica e potrà usare l'app per il periodo indicato.
            </p>
            
            <div className="space-y-2">
              <Label className="text-sm text-gray-400">Giorni di prova</Label>
              <Input
                type="number"
                min="1"
                max="90"
                value={trialDays}
                onChange={(e) => setTrialDays(parseInt(e.target.value) || 15)}
                className="bg-black border-[#282c34]"
              />
              <p className="text-xs text-disabled">
                La prova scadrà il: {new Date(Date.now() + trialDays * 24 * 60 * 60 * 1000).toLocaleDateString('it-IT')}
              </p>
            </div>
          </div>
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setShowTrialModal(false)}
              className="border-[#282c34] text-gray-400"
            >
              Annulla
            </Button>
            <Button
              onClick={handleSetTrial}
              disabled={settingTrial}
              className="ht-btn ht-btn-primary gap-2"
            >
              <Timer size={14} />
              {settingTrial ? 'Avvio...' : `Avvia ${trialDays} Giorni di Prova`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* End Trial Confirmation Modal */}
      <AlertDialog open={showEndTrialConfirm} onOpenChange={setShowEndTrialConfirm}>
        <AlertDialogContent className="ht-panel text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Terminare il periodo di prova?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              Stai per terminare il periodo di prova per <span className="text-white font-medium">{endTrialUserName}</span>.
              L'utente non potrà più accedere alle funzionalità premium fino a quando non sottoscriverà un abbonamento.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel 
              className="ht-btn border-0"
              onClick={() => {
                setShowEndTrialConfirm(false);
                setEndTrialUserId(null);
                setEndTrialUserName('');
              }}
            >
              Annulla
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleEndTrial} 
              disabled={endingTrial}
              className="ht-btn ht-btn-danger"
            >
              {endingTrial ? 'Termino...' : 'Termina Prova'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Profili collegati */}
      <ProfileGroupModal
        open={showProfileGroupModal}
        onOpenChange={setShowProfileGroupModal}
        targetUser={selectedUser}
        allUsers={users}
        onChanged={fetchUsers}
      />

      <FamilyGroupModal
        open={showFamilyGroupModal}
        onOpenChange={setShowFamilyGroupModal}
        targetUser={selectedUser}
        allUsers={users}
        onChanged={fetchUsers}
      />

      {/* Reset Telegram Confirmation Modal */}
      <AlertDialog open={showResetTelegramConfirm} onOpenChange={setShowResetTelegramConfirm}>
        <AlertDialogContent className="ht-panel text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Resettare il collegamento Telegram?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              Stai per scollegare l'account Telegram di <span className="text-white font-medium">{resetTelegramUser?.name}</span>.
              L'utente dovrà ricollegare il proprio account Telegram per ricevere le notifiche.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel 
              className="ht-btn border-0"
              onClick={() => {
                setShowResetTelegramConfirm(false);
                setResetTelegramUser(null);
              }}
            >
              Annulla
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleResetTelegram} 
              disabled={resettingTelegram}
              className="ht-btn ht-btn-danger"
            >
              {resettingTelegram ? 'Resetto...' : 'Reset Telegram'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Link Telegram Modal */}
      <Dialog open={showLinkTelegramModal} onOpenChange={setShowLinkTelegramModal}>
        <DialogContent className="bg-[#0d0d0d] border-[#282c34] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="text-[#e6e9ee]" size={20} />
              Collega Telegram
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-gray-400">
              Inserisci il chat_id Telegram di <span className="text-white font-medium">{linkTelegramUser?.name}</span>
            </p>
            
            <div className="space-y-2">
              <Label className="text-sm text-gray-400">Chat ID Telegram</Label>
              <Input
                type="text"
                value={linkTelegramChatId}
                onChange={(e) => setLinkTelegramChatId(e.target.value)}
                placeholder="Es: 123456789"
                className="bg-black border-[#282c34] text-white"
              />
              <p className="text-xs text-disabled">
                L'utente può ottenere il proprio chat_id inviando /start al bot @HedgingTrakerBot
              </p>
            </div>
          </div>
          
          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              onClick={() => {
                setShowLinkTelegramModal(false);
                setLinkTelegramUser(null);
                setLinkTelegramChatId('');
              }}
              className="border-[#282c34]"
            >
              Annulla
            </Button>
            <Button
              onClick={handleLinkTelegram}
              disabled={!linkTelegramChatId.trim() || linkingTelegram}
              className="ht-btn ht-btn-primary"
            >
              {linkingTelegram ? 'Collego...' : 'Collega Telegram'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick Action Modal */}
      <Dialog open={showQuickActionModal} onOpenChange={setShowQuickActionModal}>
        <DialogContent className="bg-[#0d0d0d] border-[#282c34] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {quickActionType === 'withdrawal' ? (
                <>
                  <DollarSign className="text-[#3ddba9]" size={20} />
                  Prelievo Broker
                </>
              ) : (
                <>
                  <CreditCard className="text-[#e6e9ee]" size={20} />
                  Acquisto Account
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {quickActionUser && (
              <p className="text-sm text-gray-400">
                Invia messaggio a <span className="text-white font-medium">{quickActionUser.name}</span>
              </p>
            )}
            
            {quickActionType === 'withdrawal' ? (
              <div className="space-y-2">
                <Label className="text-sm text-gray-400">Importo ($)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="Es: 1000"
                  value={quickActionAmount}
                  onChange={(e) => setQuickActionAmount(e.target.value)}
                  className="bg-black border-[#282c34]"
                />
              </div>
            ) : (
              <div className="space-y-2">
                <Label className="text-sm text-gray-400">Numero di conti</Label>
                <Input
                  type="number"
                  min="1"
                  max="100"
                  value={quickActionNumAccounts}
                  onChange={(e) => setQuickActionNumAccounts(parseInt(e.target.value) || 1)}
                  className="bg-black border-[#282c34]"
                />
                <p className="text-xs text-[#e6e9ee]">
                  Totale: ${(quickActionNumAccounts * 750).toLocaleString()}
                </p>
              </div>
            )}
            
            <div className="space-y-2">
              <Label className="text-sm text-gray-400">Metodo</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant={quickActionMethod === 'crypto' ? 'default' : 'outline'}
                  onClick={() => setQuickActionMethod('crypto')}
                  className={quickActionMethod === 'crypto' 
                    ? 'ht-btn ht-btn-warn flex-1' 
                    : 'border-[#282c34] flex-1'}
                >
                  Crypto
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={quickActionMethod === 'bank' ? 'default' : 'outline'}
                  onClick={() => setQuickActionMethod('bank')}
                  className={quickActionMethod === 'bank' 
                    ? 'ht-btn ht-btn-primary flex-1' 
                    : 'border-[#282c34] flex-1'}
                >
                  Bonifico Bancario
                </Button>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-sm text-gray-400">Chi esegue l'azione?</Label>
              <div className="space-y-2">
                <label className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer border transition-colors ${
                  quickActionWho === 'admin' 
                    ? 'border-[rgba(61,219,169,.5)] bg-[rgba(61,219,169,.08)]' 
                    : 'border-[#282c34] hover:border-gray-600'
                }`}>
                  <input
                    type="radio"
                    name="who"
                    value="admin"
                    checked={quickActionWho === 'admin'}
                    onChange={() => setQuickActionWho('admin')}
                    className="text-[#3ddba9]"
                  />
                  <div>
                    <p className="text-sm text-white font-medium">
                      {quickActionType === 'withdrawal' ? 'Ho prelevato io' : 'Ho acquistato io'}
                    </p>
                    <p className="text-xs text-disabled">L'utente riceve conferma dell'azione completata</p>
                  </div>
                </label>
                <label className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer border transition-colors ${
                  quickActionWho === 'user' 
                    ? 'border-[rgba(240,180,90,.5)] bg-[rgba(240,180,90,.08)]' 
                    : 'border-[#282c34] hover:border-gray-600'
                }`}>
                  <input
                    type="radio"
                    name="who"
                    value="user"
                    checked={quickActionWho === 'user'}
                    onChange={() => setQuickActionWho('user')}
                    className="ht-warn"
                  />
                  <div>
                    <p className="text-sm text-white font-medium">
                      {quickActionType === 'withdrawal' ? 'Preleva tu' : 'Acquista tu'}
                    </p>
                    <p className="text-xs text-disabled">L'utente riceve istruzioni per procedere</p>
                  </div>
                </label>
              </div>
            </div>
          </div>
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setShowQuickActionModal(false)}
              className="border-[#282c34] text-gray-400"
            >
              Annulla
            </Button>
            <Button
              onClick={handleSendQuickAction}
              disabled={sendingQuickAction || (quickActionUser && !quickActionUser.telegram_chat_id)}
              className={quickActionType === 'withdrawal' 
                ? 'ht-btn ht-btn-primary gap-2' 
                : 'ht-btn ht-btn-primary gap-2'}
            >
              <Send size={14} />
              {sendingQuickAction ? 'Invio...' : 'Invia Messaggio'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Broadcast Message Modal */}
      <Dialog open={showBroadcastModal} onOpenChange={setShowBroadcastModal}>
        <DialogContent className="bg-[#0d0d0d] border-[#282c34] text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle className="text-[#e6e9ee]" size={20} />
              Invia Messaggio
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm text-gray-400">Destinatari</Label>
              <div className="space-y-2">
                {/* All users */}
                <label className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer border transition-colors ${
                  broadcastTarget === 'all' 
                    ? 'border-[#282c34] bg-[rgba(40,44,52,.4)]' 
                    : 'border-[#282c34] hover:border-gray-600'
                }`}>
                  <input
                    type="radio"
                    name="broadcast-target"
                    value="all"
                    checked={broadcastTarget === 'all'}
                    onChange={() => setBroadcastTarget('all')}
                    className="text-blue-500"
                  />
                  <div>
                    <p className="text-sm text-white font-medium">Tutti gli utenti</p>
                    <p className="text-xs text-disabled">Gestiti + Autonomi ({users.length})</p>
                  </div>
                </label>
                
                {/* Active only */}
                <label className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer border transition-colors ${
                  broadcastTarget === 'active' 
                    ? 'border-[rgba(61,219,169,.5)] bg-[rgba(61,219,169,.08)]' 
                    : 'border-[#282c34] hover:border-gray-600'
                }`}>
                  <input
                    type="radio"
                    name="broadcast-target"
                    value="active"
                    checked={broadcastTarget === 'active'}
                    onChange={() => setBroadcastTarget('active')}
                    className="text-[#3ddba9]"
                  />
                  <div>
                    <p className="text-sm text-white font-medium">Solo utenti attivi</p>
                    <p className="text-xs text-disabled">Utenti con stato attivo ({users.filter(u => u.is_active !== false).length})</p>
                  </div>
                </label>
                
                {/* Managed only */}
                <label className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer border transition-colors ${
                  broadcastTarget === 'managed' 
                    ? 'border-[rgba(61,219,169,.5)] bg-[rgba(61,219,169,.08)]' 
                    : 'border-[#282c34] hover:border-gray-600'
                }`}>
                  <input
                    type="radio"
                    name="broadcast-target"
                    value="managed"
                    checked={broadcastTarget === 'managed'}
                    onChange={() => setBroadcastTarget('managed')}
                    className="text-[#3ddba9]"
                  />
                  <div>
                    <p className="text-sm text-white font-medium">Solo utenti gestiti</p>
                    <p className="text-xs text-disabled">Utenti con delega all'admin ({users.filter(u => u.management_type === 'managed').length})</p>
                  </div>
                </label>
                
                {/* Autonomous only */}
                <label className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer border transition-colors ${
                  broadcastTarget === 'autonomous' 
                    ? 'border-[rgba(61,219,169,.5)] bg-[rgba(61,219,169,.08)]' 
                    : 'border-[#282c34] hover:border-gray-600'
                }`}>
                  <input
                    type="radio"
                    name="broadcast-target"
                    value="autonomous"
                    checked={broadcastTarget === 'autonomous'}
                    onChange={() => setBroadcastTarget('autonomous')}
                    className="text-[#3ddba9]"
                  />
                  <div>
                    <p className="text-sm text-white font-medium">Solo utenti autonomi</p>
                    <p className="text-xs text-disabled">Utenti che gestiscono in autonomia ({users.filter(u => u.management_type !== 'managed').length})</p>
                  </div>
                </label>
                
                {/* Selected users */}
                <label className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer border transition-colors ${
                  broadcastTarget === 'selected' 
                    ? 'border-[rgba(240,180,90,.5)] bg-[rgba(240,180,90,.08)]' 
                    : 'border-[#282c34] hover:border-gray-600'
                }`}>
                  <input
                    type="radio"
                    name="broadcast-target"
                    value="selected"
                    checked={broadcastTarget === 'selected'}
                    onChange={() => setBroadcastTarget('selected')}
                    className="ht-warn"
                  />
                  <div>
                    <p className="text-sm text-white font-medium">Utenti specifici</p>
                    <p className="text-xs text-disabled">
                      {selectedUserIds.length > 0 
                        ? `${selectedUserIds.length} utenti selezionati` 
                        : 'Seleziona dalla lista sotto'}
                    </p>
                  </div>
                </label>
              </div>
            </div>
            
            {/* User selection grid - only when 'selected' target */}
            {broadcastTarget === 'selected' && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm text-gray-400">Seleziona Utenti</Label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedUserIds(users.filter(u => u.telegram_chat_id).map(u => u.id))}
                      className="text-xs text-[#e6e9ee] h-7"
                    >
                      Seleziona tutti
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedUserIds([])}
                      className="text-xs text-gray-400 h-7"
                    >
                      Deseleziona
                    </Button>
                  </div>
                </div>
                <div className="max-h-40 overflow-y-auto bg-black/30 rounded-lg p-2 space-y-1">
                  {users.filter(u => u.telegram_chat_id).map(user => (
                    <label 
                      key={user.id}
                      className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                        selectedUserIds.includes(user.id) 
                          ? 'bg-[rgba(240,180,90,.08)] border border-[rgba(240,180,90,.4)]' 
                          : 'hover:bg-[#11161d]'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedUserIds.includes(user.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedUserIds([...selectedUserIds, user.id]);
                          } else {
                            setSelectedUserIds(selectedUserIds.filter(id => id !== user.id));
                          }
                        }}
                        className="rounded ht-warn"
                      />
                      <span className="text-sm text-white">{user.name}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                        user.is_active !== false ? 'bg-[rgba(61,219,169,.14)] text-[#3ddba9]' : 'bg-gray-500/20 text-gray-400'
                      }`}>
                        {user.is_active !== false ? 'Attivo' : 'Inattivo'}
                      </span>
                    </label>
                  ))}
                  {users.filter(u => u.telegram_chat_id).length === 0 && (
                    <p className="text-xs text-disabled text-center py-2">Nessun utente con Telegram collegato</p>
                  )}
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              <Label className="text-sm text-gray-400">Messaggio</Label>
              <textarea
                value={broadcastMessage}
                onChange={(e) => setBroadcastMessage(e.target.value)}
                placeholder="Scrivi il messaggio da inviare..."
                className="w-full bg-black border border-[#282c34] rounded-lg p-3 text-white text-sm min-h-[120px] resize-none focus:outline-none focus:border-blue-500"
              />
              <p className="text-xs text-disabled">
                Il messaggio verrà inviato via Telegram. Gli utenti senza Telegram collegato verranno saltati.
              </p>
            </div>
          </div>
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => {
                setShowBroadcastModal(false);
                setBroadcastMessage('');
                setBroadcastTarget('all');
                setSelectedUserIds([]);
              }}
              className="border-[#282c34] text-gray-400"
            >
              Annulla
            </Button>
            <Button
              onClick={handleSendBroadcast}
              disabled={sendingBroadcast || !broadcastMessage.trim() || (broadcastTarget === 'selected' && selectedUserIds.length === 0)}
              className="ht-btn ht-btn-primary gap-2"
            >
              <Send size={14} />
              {sendingBroadcast ? 'Invio...' : broadcastTarget === 'selected' ? `Invia a ${selectedUserIds.length}` : 'Invia'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Report Generation Modal */}
      <Dialog open={showReportModal} onOpenChange={setShowReportModal}>
        <DialogContent className="ht-panel text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <FileText size={18} className="text-[#3ddba9]" />
              Genera Report — {reportUserName}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-2">
            {/* Period Type Selection */}
            <div className="space-y-2">
              <Label className="text-gray-300 text-sm">Tipo periodo</Label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="periodType"
                    checked={reportPeriodType === 'full'}
                    onChange={() => handlePeriodTypeChange('full')}
                    className="w-4 h-4 accent-usdt"
                  />
                  <span className="text-sm text-gray-300">Storico completo</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="periodType"
                    checked={reportPeriodType === 'custom'}
                    onChange={() => handlePeriodTypeChange('custom')}
                    className="w-4 h-4 accent-usdt"
                  />
                  <span className="text-sm text-gray-300">Periodo personalizzato</span>
                </label>
              </div>
            </div>

            {/* Date Range (visible only for custom period) */}
            {reportPeriodType === 'custom' && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-gray-400 text-xs">Dal</Label>
                  <Input
                    type="date"
                    value={reportDateFrom}
                    onChange={(e) => handleDateChange('from', e.target.value)}
                    className="bg-[#11161d] border-[#282c34] text-white text-sm h-9"
                    data-testid="report-date-from"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-gray-400 text-xs">Al</Label>
                  <Input
                    type="date"
                    value={reportDateTo}
                    onChange={(e) => handleDateChange('to', e.target.value)}
                    className="bg-[#11161d] border-[#282c34] text-white text-sm h-9"
                    data-testid="report-date-to"
                  />
                </div>
              </div>
            )}

            {/* Report Title */}
            <div className="space-y-1">
              <Label className="text-gray-300 text-sm">Titolo del report</Label>
              <Input
                type="text"
                value={reportTitle}
                onChange={(e) => setReportTitle(e.target.value)}
                placeholder="Titolo del report"
                className="bg-[#11161d] border-[#282c34] text-white text-sm"
                data-testid="report-title-input"
              />
            </div>

            {/* Recipient Selection */}
            <div className="space-y-2">
              <Label className="text-gray-300 text-sm">Destinatario</Label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="reportRecipient"
                    checked={!reportSendToAdmin}
                    onChange={() => setReportSendToAdmin(false)}
                    className="w-4 h-4 accent-usdt"
                  />
                  <span className="text-sm text-gray-300">Invia all'utente</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="reportRecipient"
                    checked={reportSendToAdmin}
                    onChange={() => setReportSendToAdmin(true)}
                    className="w-4 h-4 accent-usdt"
                  />
                  <span className="text-sm text-gray-300">Invia a me (admin)</span>
                </label>
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowReportModal(false)}
              className="border-[#282c34] text-gray-400"
            >
              Annulla
            </Button>
            <Button
              onClick={handleGenerateReport}
              disabled={generatingReport}
              className="ht-btn ht-btn-primary gap-2"
              data-testid="generate-report-submit"
            >
              {generatingReport ? (
                <RefreshCw size={14} className="animate-spin" />
              ) : (
                <FileText size={14} />
              )}
              {generatingReport ? 'Generazione...' : 'Genera e Invia'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Global Report Modal */}
      <Dialog open={showGlobalReportModal} onOpenChange={setShowGlobalReportModal}>
        <DialogContent className="ht-panel text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <FileText size={18} className="text-[#3ddba9]" />
              Genera Report Globale
            </DialogTitle>
            <p className="text-sm text-gray-400 mt-1">PDF con tutti gli utenti inviato su Telegram</p>
          </DialogHeader>
          
          <div className="space-y-4 py-2">
            {/* Period Type Selection */}
            <div className="space-y-2">
              <Label className="text-gray-300 text-sm">Tipo periodo</Label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="globalPeriodType"
                    checked={globalReportPeriodType === 'full'}
                    onChange={() => handleGlobalPeriodTypeChange('full')}
                    className="w-4 h-4 accent-usdt"
                  />
                  <span className="text-sm text-gray-300">Storico completo</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="globalPeriodType"
                    checked={globalReportPeriodType === 'custom'}
                    onChange={() => handleGlobalPeriodTypeChange('custom')}
                    className="w-4 h-4 accent-usdt"
                  />
                  <span className="text-sm text-gray-300">Periodo personalizzato</span>
                </label>
              </div>
            </div>

            {/* Date Range (visible only for custom period) */}
            {globalReportPeriodType === 'custom' && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-gray-400 text-xs">Dal</Label>
                  <Input
                    type="date"
                    value={globalReportDateFrom}
                    onChange={(e) => handleGlobalDateChange('from', e.target.value)}
                    className="bg-[#11161d] border-[#282c34] text-white text-sm h-9"
                    data-testid="global-report-date-from"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-gray-400 text-xs">Al</Label>
                  <Input
                    type="date"
                    value={globalReportDateTo}
                    onChange={(e) => handleGlobalDateChange('to', e.target.value)}
                    className="bg-[#11161d] border-[#282c34] text-white text-sm h-9"
                    data-testid="global-report-date-to"
                  />
                </div>
              </div>
            )}

            {/* Report Title */}
            <div className="space-y-1">
              <Label className="text-gray-300 text-sm">Titolo del report</Label>
              <Input
                type="text"
                value={globalReportTitle}
                onChange={(e) => setGlobalReportTitle(e.target.value)}
                placeholder="Titolo del report"
                className="bg-[#11161d] border-[#282c34] text-white text-sm"
                data-testid="global-report-title-input"
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowGlobalReportModal(false)}
              className="border-[#282c34] text-gray-400"
            >
              Annulla
            </Button>
            <Button
              onClick={handleGenerateGlobalReport}
              disabled={generatingGlobalReport}
              className="ht-btn ht-btn-primary gap-2"
              data-testid="global-report-submit"
            >
              {generatingGlobalReport ? (
                <RefreshCw size={14} className="animate-spin" />
              ) : (
                <FileText size={14} />
              )}
              {generatingGlobalReport ? 'Generazione...' : 'Genera e Invia'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Nomina Admin Modal */}
      <AlertDialog open={showNominateModal} onOpenChange={setShowNominateModal}>
        <AlertDialogContent className="ht-panel text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Nomina Admin Secondario</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              Sei sicuro di voler nominare <span className="text-[#f0b45a] font-semibold">{nominateUserName}</span> come Admin Secondario?
              <br /><br />
              Potrà gestire i propri utenti e riceverà un link di invito personale.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel 
              className="ht-btn border-0 hover:bg-gray-700"
              onClick={() => {
                setShowNominateModal(false);
                setNominateUserId(null);
                setNominateUserName('');
              }}
            >
              Annulla
            </AlertDialogCancel>
            <AlertDialogAction 
              className="bg-yellow-600 text-white hover:bg-yellow-700"
              onClick={handleNominateAdmin}
              disabled={nominating}
            >
              {nominating ? 'Nomina in corso...' : '👑 Conferma Nomina'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Revoca Admin Modal */}
      <AlertDialog open={showRevokeModal} onOpenChange={setShowRevokeModal}>
        <AlertDialogContent className="ht-panel text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Revoca Permessi Admin</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              Sei sicuro di voler revocare i permessi admin a <span className="text-red-400 font-semibold">{revokeUserName}</span>?
              <br /><br />
              L'utente tornerà ad essere un utente normale e i suoi utenti saranno riassegnati a te.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel 
              className="ht-btn border-0 hover:bg-gray-700"
              onClick={() => {
                setShowRevokeModal(false);
                setRevokeUserId(null);
                setRevokeUserName('');
              }}
            >
              Annulla
            </AlertDialogCancel>
            <AlertDialogAction 
              className="bg-red-600 text-white hover:bg-red-700"
              onClick={handleRevokeAdmin}
              disabled={revoking}
            >
              {revoking ? 'Revoca in corso...' : '🚫 Conferma Revoca'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Trasferisci Utente Modal */}
      <Dialog open={showTransferModal} onOpenChange={setShowTransferModal}>
        <DialogContent className="bg-[#0d0d0d] border-[#282c34] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="text-[#e6e9ee]" size={20} />
              Trasferisci Utente
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-gray-400">
              Trasferisci <span className="text-white font-medium">{transferUserName}</span> ad un altro admin
            </p>
            
            <div className="space-y-2">
              <Label className="text-sm text-gray-400">Seleziona Admin</Label>
              <select
                value={transferToAdmin}
                onChange={(e) => setTransferToAdmin(e.target.value)}
                className="w-full bg-black border border-[#282c34] rounded-md px-3 py-2 text-white"
              >
                <option value="">Seleziona...</option>
                {availableAdmins.map(admin => (
                  <option key={admin.user_id} value={admin.user_id}>
                    {admin.name} {admin.role === 'admin_primary' ? '(Primary)' : '(Secondary)'}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              onClick={() => {
                setShowTransferModal(false);
                setTransferUserId(null);
                setTransferUserName('');
                setTransferToAdmin('');
              }}
              className="border-[#282c34]"
            >
              Annulla
            </Button>
            <Button
              onClick={handleTransferUser}
              disabled={!transferToAdmin || transferring}
              className="ht-btn ht-btn-primary"
            >
              {transferring ? 'Trasferimento...' : '↔️ Trasferisci'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
